import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CalendarRange, Check, ClipboardList, Clock3, MapPin, PackageCheck, Pencil, Plus, Route, Save, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { BrandAmbassadorPicker } from '../../components/BrandAmbassadorPicker';
import { CityMultiSelect } from '../../components/CityMultiSelect';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import { MerchandisingRoutesPanel } from '../merchandising/MerchandisingRoutesPanel';
import { ManualSalesImportDialog } from '../sales/ManualSalesImportDialog';
import { ManualSalesEntryDialog } from '../sales/ManualSalesEntryDialog';
import type {
  ActivitySession,
  ActivitySite,
  Assignment,
  Campaign,
  CampaignActivitySite,
  CampaignLocation,
  CampaignLocationProductFamilyTarget,
  CampaignLocationProductTarget,
  CampaignFreeSampleDeal,
  CampaignProduct,
  CampaignProductFamilyTarget,
  CampaignShift,
  CampaignSurvey,
  PakistanCity,
  Product,
  ScheduleRuleTemplate,
  ShiftSchedule,
  ShiftScheduleRevisionImpact,
  SurveyCampaignAssignment,
  SurveyTemplateVersion,
  TalentProfile,
  Venue,
} from '../../types/domain';

interface LocationDraft {
  venue: string;
  allowed_radius_m: string;
}

interface ActivitySiteDraft {
  catalogue_site: string;
  kind: ActivitySite['kind'];
  name: string;
  campus_name: string;
  address: string;
  area: string;
  city: string;
  province: string;
  latitude: string;
  longitude: string;
  contact_name: string;
  contact_phone: string;
  allowed_radius_m: string;
}

interface ActivitySessionDraft {
  shift: string;
  lead: string;
  backup_lead: string;
  notes: string;
}

interface ShiftDraft {
  location: string;
  starts_at: string;
  ends_at: string;
  checkin_open_minutes: string;
  late_after_minutes: string;
  checkout_grace_minutes: string;
}

interface ScheduleRuleDraft {
  template_number: number | null;
  name: string;
  weekdays: string[];
  start_time: string;
  end_time: string;
}

interface ScheduleDraft {
  name: string;
  locations: string[];
  starts_on: string;
  ends_on: string;
  checkin_open_minutes: string;
  late_after_minutes: string;
  checkout_grace_minutes: string;
  rules: ScheduleRuleDraft[];
}

interface ProductTargetDraft {
  product: string;
  target_value: string;
  target_metric: CampaignProduct['target_metric'];
  target_period: CampaignProduct['target_period'];
}

interface ProductFamilyTargetDraft {
  product_family: string;
  target_value: string;
  target_metric: CampaignProductFamilyTarget['target_metric'];
  target_period: CampaignProductFamilyTarget['target_period'];
}

interface FreeSampleDealDraft {
  qualifying_quantity: string;
  sample_quantity: string;
  sample_products: string[];
  is_active: boolean;
}

const emptyFreeSampleDeal: FreeSampleDealDraft = {
  qualifying_quantity: '1',
  sample_quantity: '1',
  sample_products: [],
  is_active: true,
};

type StoreAllocationTarget =
  | {
    key: string;
    kind: 'product';
    name: string;
    detail: string;
    metric: CampaignProduct['target_metric'];
    period: CampaignProduct['target_period'];
    targetValue: number;
    target: CampaignProduct;
  }
  | {
    key: string;
    kind: 'family';
    name: string;
    detail: string;
    metric: CampaignProductFamilyTarget['target_metric'];
    period: CampaignProductFamilyTarget['target_period'];
    targetValue: number;
    target: CampaignProductFamilyTarget;
  };

type DeleteTarget =
  | { kind: 'schedule'; schedule: ShiftSchedule }
  | { kind: 'shift'; shift: CampaignShift };

type DetachTarget =
  | { kind: 'store'; location: CampaignLocation }
  | { kind: 'product'; product: CampaignProduct };

type CampaignSetupTab = 'campaign-stores' | 'shift-schedules' | 'products-targets' | 'store-targets' | 'survey' | 'merchandising-routes';

const campaignSetupTabs: Array<{ id: CampaignSetupTab; label: string }> = [
  { id: 'campaign-stores', label: 'Locations and sessions' },
  { id: 'shift-schedules', label: 'Shift schedules' },
  { id: 'products-targets', label: 'Products and targets' },
  { id: 'store-targets', label: 'Store targets' },
  { id: 'survey', label: 'Survey' },
  { id: 'merchandising-routes', label: 'Merchandising routes' },
];

interface SurveyConfigurationDraft {
  template_version: string;
  territory_label: string;
}

interface SurveyCampaignAssignmentDraft {
  brand_ambassador: string;
  starts_on: string;
  ends_on: string;
  allowed_cities: string[];
  allowed_areas: string;
}

interface SurveyUpgradeDraft {
  template_version: string;
  effective_on: string;
}

interface SurveyAuthorizationDateDraft {
  starts_on: string;
  ends_on: string;
}

interface CampaignSurveyUpgradeResult {
  campaign_survey: CampaignSurvey;
  copied_assignments: number;
  skipped_assignments: number;
}

const emptySurveyConfiguration: SurveyConfigurationDraft = {
  template_version: '',
  territory_label: '',
};

const emptySurveyCampaignAssignment: SurveyCampaignAssignmentDraft = {
  brand_ambassador: '',
  starts_on: '',
  ends_on: '',
  allowed_cities: [],
  allowed_areas: '',
};

const emptySurveyUpgrade: SurveyUpgradeDraft = {
  template_version: '',
  effective_on: '',
};

const emptySurveyAuthorizationDates: SurveyAuthorizationDateDraft = {
  starts_on: '',
  ends_on: '',
};

const emptyLocation: LocationDraft = {
  venue: '',
  allowed_radius_m: '50',
};

const emptyActivitySite: ActivitySiteDraft = {
  catalogue_site: '',
  kind: 'university',
  name: '',
  campus_name: '',
  address: '',
  area: '',
  city: '',
  province: '',
  latitude: '',
  longitude: '',
  contact_name: '',
  contact_phone: '',
  allowed_radius_m: '100',
};

const emptyActivitySession: ActivitySessionDraft = {
  shift: '',
  lead: '',
  backup_lead: '',
  notes: '',
};

const emptyShift: ShiftDraft = {
  location: '',
  starts_at: '',
  ends_at: '',
  checkin_open_minutes: '30',
  late_after_minutes: '15',
  checkout_grace_minutes: '120',
};

const emptyScheduleRule: ScheduleRuleDraft = {
  template_number: null,
  name: '',
  weekdays: [],
  start_time: '',
  end_time: '',
};

const emptySchedule: ScheduleDraft = {
  name: 'Standard schedule',
  locations: [],
  starts_on: '',
  ends_on: '',
  checkin_open_minutes: '30',
  late_after_minutes: '15',
  checkout_grace_minutes: '120',
  rules: [emptyScheduleRule],
};

const weekdayOptions = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

function ruleDraftFromTemplate(template: ScheduleRuleTemplate): ScheduleRuleDraft {
  return {
    template_number: template.rule_number,
    name: template.name,
    weekdays: weekdayOptions
      .filter((weekday) => template[weekday.value as keyof ScheduleRuleTemplate] === true)
      .map((weekday) => weekday.value),
    start_time: template.start_time.slice(0, 5),
    end_time: template.end_time.slice(0, 5),
  };
}

function scheduleRuleRequestRules(rules: ScheduleRuleDraft[]) {
  return rules.map((rule) => ({
    name: rule.name,
    weekdays: rule.weekdays,
    start_time: rule.start_time,
    end_time: rule.end_time,
  }));
}

const emptyProductTarget: ProductTargetDraft = {
  product: '',
  target_value: '',
  target_metric: 'pieces',
  target_period: 'campaign',
};

const emptyProductFamilyTarget: ProductFamilyTargetDraft = {
  product_family: '',
  target_value: '',
  target_metric: 'volume',
  target_period: 'campaign',
};

const targetMetricLabels: Record<CampaignProduct['target_metric'], string> = {
  pieces: 'Pieces',
  mass: 'Mass (kg)',
  volume: 'Volume (L)',
  revenue: 'Revenue (PKR)',
};

const targetMetricUnits: Record<CampaignProduct['target_metric'], string> = {
  pieces: 'pieces',
  mass: 'kg',
  volume: 'L',
  revenue: 'PKR',
};

const targetPeriodLabels: Record<CampaignProduct['target_period'], string> = {
  campaign: 'Campaign total',
  monthly: 'Monthly',
};

function formatTarget(
  value: string | number | null,
  metric: CampaignProduct['target_metric'],
  period: CampaignProduct['target_period'] = 'campaign',
) {
  if (value === null || value === '') return 'Not set';
  const formatted = Number(value).toLocaleString('en-PK', { maximumFractionDigits: 3 });
  const periodSuffix = period === 'monthly' ? ' / month' : '';
  if (metric === 'revenue') return `PKR ${formatted}${periodSuffix}`;
  return `${formatted} ${metric === 'mass' ? 'kg' : metric === 'volume' ? 'L' : 'pieces'}${periodSuffix}`;
}

function formatTargetTotals(
  values: Array<{
    metric: CampaignProduct['target_metric'];
    period: CampaignProduct['target_period'];
    value: number;
  }>,
) {
  const totals = values.reduce<Record<string, number>>((current, item) => {
    const key = `${item.metric}:${item.period}`;
    return { ...current, [key]: (current[key] ?? 0) + item.value };
  }, {});
  const formatted = Object.entries(totals).map(([key, value]) => {
    const [metric, period] = key.split(':') as [
      CampaignProduct['target_metric'],
      CampaignProduct['target_period'],
    ];
    return formatTarget(value, metric, period);
  });
  return formatted.length > 0 ? formatted.join(' · ') : '—';
}

const dateTimeFormatter = new Intl.DateTimeFormat('en-PK', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Karachi',
});

const dateFormatter = new Intl.DateTimeFormat('en-PK', {
  dateStyle: 'medium',
  timeZone: 'Asia/Karachi',
});

const timeFormatter = new Intl.DateTimeFormat('en-PK', {
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
});

function formatScheduleTime(value: string) {
  return timeFormatter.format(new Date(`1970-01-01T${value}Z`));
}

function localDateTime(value: string) {
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function pakistanDateInput(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Karachi',
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function dateInputDaysAfter(value: string, days: number) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function commaSeparatedValues(value: string) {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function minimumRevisionDate(schedule: ShiftSchedule) {
  return [pakistanDateInput(), dateInputDaysAfter(schedule.starts_on, 1)]
    .sort()
    .at(-1) ?? schedule.starts_on;
}

export function CampaignSetupPage() {
  const { campaignId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken, selectedWorkspace, capabilities } = useAuth();
  const [showSalesImport, setShowSalesImport] = useState(false);
  const [showSalesEntry, setShowSalesEntry] = useState(false);
  const [savedSaleReference, setSavedSaleReference] = useState<string | null>(null);
  const [salesImportCount, setSalesImportCount] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const requestedTab = searchParams.get('tab');
  const activeTab = campaignSetupTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab as CampaignSetupTab
    : 'campaign-stores';
  const [editingLocation, setEditingLocation] = useState<CampaignLocation | 'new' | null>(null);
  const [locationDraft, setLocationDraft] = useState<LocationDraft>(emptyLocation);
  const [editingActivitySite, setEditingActivitySite] = useState<CampaignActivitySite | 'new' | null>(null);
  const [activitySiteDraft, setActivitySiteDraft] = useState<ActivitySiteDraft>(emptyActivitySite);
  const [activitySiteCatalogueSearch, setActivitySiteCatalogueSearch] = useState('');
  const [debouncedActivitySiteCatalogueSearch, setDebouncedActivitySiteCatalogueSearch] = useState('');
  const [activitySiteCataloguePage, setActivitySiteCataloguePage] = useState(1);
  const [selectedActivitySiteCatalogueEntry, setSelectedActivitySiteCatalogueEntry] = useState<ActivitySite | null>(null);
  const [editingActivitySession, setEditingActivitySession] = useState(false);
  const [activitySessionDraft, setActivitySessionDraft] = useState<ActivitySessionDraft>(emptyActivitySession);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venueSearch, setVenueSearch] = useState('');
  const [debouncedVenueSearch, setDebouncedVenueSearch] = useState('');
  const [venuePage, setVenuePage] = useState(1);
  const [editingShift, setEditingShift] = useState<CampaignShift | 'new' | null>(null);
  const [shiftDraft, setShiftDraft] = useState<ShiftDraft>(emptyShift);
  const [editingSchedule, setEditingSchedule] = useState<ShiftSchedule | 'new' | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft>(emptySchedule);
  const [scheduleLocationSearch, setScheduleLocationSearch] = useState('');
  const [scheduleFilterSearch, setScheduleFilterSearch] = useState('');
  const [debouncedScheduleFilterSearch, setDebouncedScheduleFilterSearch] = useState('');
  const [scheduleFilterLocation, setScheduleFilterLocation] = useState('');
  const [scheduleFilterActiveOn, setScheduleFilterActiveOn] = useState(() => pakistanDateInput());
  const [isScheduleRevision, setIsScheduleRevision] = useState(false);
  const [revisionEffectiveFrom, setRevisionEffectiveFrom] = useState('');
  const [revisionReason, setRevisionReason] = useState('');
  const [revisionImpact, setRevisionImpact] = useState<ShiftScheduleRevisionImpact | null>(null);
  const [shorteningSchedule, setShorteningSchedule] = useState<ShiftSchedule | null>(null);
  const [shortenEndsOn, setShortenEndsOn] = useState('');
  const [shortenReason, setShortenReason] = useState('');
  const [showIndividualShifts, setShowIndividualShifts] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [detachTarget, setDetachTarget] = useState<DetachTarget | null>(null);
  const [editingProductTarget, setEditingProductTarget] = useState<CampaignProduct | 'new' | null>(null);
  const [editingFreeSampleDeal, setEditingFreeSampleDeal] = useState<CampaignProduct | null>(null);
  const [freeSampleDealDraft, setFreeSampleDealDraft] = useState<FreeSampleDealDraft>(emptyFreeSampleDeal);
  const [productTargetDraft, setProductTargetDraft] = useState<ProductTargetDraft>(emptyProductTarget);
  const [confirmProductPeriodChange, setConfirmProductPeriodChange] = useState(false);
  const [editingProductFamilyTarget, setEditingProductFamilyTarget] = useState<CampaignProductFamilyTarget | 'new' | null>(null);
  const [familyTargetRemoval, setFamilyTargetRemoval] = useState<CampaignProductFamilyTarget | null>(null);
  const [productFamilyTargetDraft, setProductFamilyTargetDraft] = useState<ProductFamilyTargetDraft>(emptyProductFamilyTarget);
  const [confirmFamilyPeriodChange, setConfirmFamilyPeriodChange] = useState(false);
  const [locationTargetDrafts, setLocationTargetDrafts] = useState<Record<string, string>>({});
  const [locationFamilyTargetDrafts, setLocationFamilyTargetDrafts] = useState<Record<string, string>>({});
  const [selectedStoreTargetKey, setSelectedStoreTargetKey] = useState('');
  const [storeTargetSearch, setStoreTargetSearch] = useState('');
  const [editingSurvey, setEditingSurvey] = useState(false);
  const [surveyDraft, setSurveyDraft] = useState<SurveyConfigurationDraft>(emptySurveyConfiguration);
  const [authorizingSurveyBA, setAuthorizingSurveyBA] = useState(false);
  const [surveyAssignmentDraft, setSurveyAssignmentDraft] = useState<SurveyCampaignAssignmentDraft>(
    emptySurveyCampaignAssignment,
  );
  const [surveyTalentSearch, setSurveyTalentSearch] = useState('');
  const [debouncedSurveyTalentSearch, setDebouncedSurveyTalentSearch] = useState('');
  const [surveyTalentPage, setSurveyTalentPage] = useState(1);
  const [selectedSurveyTalent, setSelectedSurveyTalent] = useState<TalentProfile | null>(null);
  const [cancellingSurveyAssignment, setCancellingSurveyAssignment] = useState<SurveyCampaignAssignment | null>(null);
  const [editingSurveyAssignment, setEditingSurveyAssignment] = useState<SurveyCampaignAssignment | null>(null);
  const [surveyAuthorizationDateDraft, setSurveyAuthorizationDateDraft] = useState<SurveyAuthorizationDateDraft>(emptySurveyAuthorizationDates);
  const [surveyAssignmentCancellationReason, setSurveyAssignmentCancellationReason] = useState('');
  const [upgradingSurvey, setUpgradingSurvey] = useState(false);
  const [surveyUpgradeDraft, setSurveyUpgradeDraft] = useState<SurveyUpgradeDraft>(emptySurveyUpgrade);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedVenueSearch(venueSearch.trim()),
      250,
    );
    return () => window.clearTimeout(timeout);
  }, [venueSearch]);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedActivitySiteCatalogueSearch(activitySiteCatalogueSearch.trim()),
      250,
    );
    return () => window.clearTimeout(timeout);
  }, [activitySiteCatalogueSearch]);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSurveyTalentSearch(surveyTalentSearch.trim()),
      250,
    );
    return () => window.clearTimeout(timeout);
  }, [surveyTalentSearch]);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedScheduleFilterSearch(scheduleFilterSearch.trim()),
      250,
    );
    return () => window.clearTimeout(timeout);
  }, [scheduleFilterSearch]);
  const campaign = useQuery({
    queryKey: ['campaign', campaignId, accessToken],
    enabled: Boolean(accessToken && campaignId),
    queryFn: () => apiRequest<Campaign>(`/campaigns/${campaignId}/`, {
      ...(accessToken ? { accessToken } : {}),
    }),
  });
  const locations = useApiCollection<CampaignLocation>(
    'campaign-locations',
    `/locations/?campaign=${campaignId}&page_size=100`,
    accessToken,
  );
  const activitySites = useApiCollection<CampaignActivitySite>(
    'campaign-activity-sites',
    `/campaign-activity-sites/?campaign=${campaignId}&page_size=100&ordering=city,name`,
    accessToken,
    activeTab === 'campaign-stores',
  );
  const activitySiteCatalogueParams = new URLSearchParams({
    available_for_campaign: campaignId,
    page: String(activitySiteCataloguePage),
    page_size: '20',
    ordering: 'city,name,campus_name',
  });
  if (debouncedActivitySiteCatalogueSearch) activitySiteCatalogueParams.set('search', debouncedActivitySiteCatalogueSearch);
  const activitySiteCatalogue = useApiCollection<ActivitySite>(
    'campaign-available-activity-sites',
    `/activity-sites/?${activitySiteCatalogueParams.toString()}`,
    accessToken,
    editingActivitySite === 'new',
  );
  const activitySessions = useApiCollection<ActivitySession>(
    'campaign-activity-sessions',
    `/activity-sessions/?campaign=${campaignId}&page_size=100&ordering=-shift__starts_at`,
    accessToken,
    activeTab === 'campaign-stores',
  );
  const activityShifts = useApiCollection<CampaignShift>(
    'campaign-activity-shifts',
    `/shifts/?campaign=${campaignId}&page_size=500&ordering=starts_at`,
    accessToken,
    activeTab === 'campaign-stores',
  );
  const campaignAssignments = useApiCollection<Assignment>(
    'campaign-activity-assignments',
    `/assignments/?campaign=${campaignId}&page_size=500&ordering=brand_ambassador__full_name`,
    accessToken,
    activeTab === 'campaign-stores',
  );
  const venueParams = new URLSearchParams({
    available_for_campaign: campaignId,
    page: String(venuePage),
    page_size: '20',
  });
  if (debouncedVenueSearch) venueParams.set('search', debouncedVenueSearch);
  const venues = useApiCollection<Venue>(
    'campaign-available-venues',
    `/venues/?${venueParams.toString()}`,
    accessToken,
    editingLocation === 'new',
  );
  const shifts = useApiCollection<CampaignShift>(
    'campaign-shifts',
    `/shifts/?campaign=${campaignId}&manual=true&page_size=100&ordering=starts_at`,
    accessToken,
    activeTab === 'shift-schedules',
  );
  const scheduleParams = new URLSearchParams({
    campaign: campaignId,
    page_size: '100',
    ordering: '-starts_on,-created_at',
  });
  if (debouncedScheduleFilterSearch) scheduleParams.set('search', debouncedScheduleFilterSearch);
  if (scheduleFilterLocation) scheduleParams.set('location', scheduleFilterLocation);
  if (scheduleFilterActiveOn) scheduleParams.set('active_on', scheduleFilterActiveOn);
  const schedules = useApiCollection<ShiftSchedule>(
    'campaign-shift-schedules',
    `/shift-schedules/?${scheduleParams.toString()}`,
    accessToken,
    activeTab === 'shift-schedules',
  );
  const campaignSurveys = useApiCollection<CampaignSurvey>(
    'campaign-surveys',
    `/campaign-surveys/?campaign=${campaignId}&page_size=100`,
    accessToken,
    activeTab === 'survey',
  );
  const campaignSurveyConfigurations = campaignSurveys.data?.results ?? [];
  const campaignSurvey = campaignSurveyConfigurations[0] ?? null;
  const surveyAssignments = useApiCollection<SurveyCampaignAssignment>(
    'campaign-survey-assignments',
    `/survey-campaign-assignments/?campaign_survey=${campaignSurvey?.id ?? ''}&page_size=100&ordering=brand_ambassador__full_name`,
    accessToken,
    activeTab === 'survey' && Boolean(campaignSurvey),
  );
  const surveyTalentParams = new URLSearchParams({
    assignment_eligible: 'true',
    ordering: 'verification_rank,availability_rank,display_name',
    page: String(surveyTalentPage),
    page_size: '20',
  });
  if (debouncedSurveyTalentSearch) {
    surveyTalentParams.set(
      /^BA-\d+$/i.test(debouncedSurveyTalentSearch) ? 'reference' : 'search',
      debouncedSurveyTalentSearch,
    );
  }
  const surveyTalent = useApiCollection<TalentProfile>(
    'campaign-survey-talent',
    `/talent/?${surveyTalentParams.toString()}`,
    accessToken,
    activeTab === 'survey' && authorizingSurveyBA,
  );
  const pakistanCities = useApiCollection<PakistanCity>(
    'pakistan-cities',
    '/pakistan-cities/?page_size=100&ordering=province,name',
    accessToken,
    activeTab === 'survey' && authorizingSurveyBA,
  );
  const surveyTemplateVersions = useApiCollection<SurveyTemplateVersion>(
    'campaign-survey-template-versions',
    `/survey-template-versions/?client=${campaign.data?.client ?? ''}&status=published&template_is_active=true&page_size=100&ordering=template__name,-version`,
    accessToken,
    activeTab === 'survey' && Boolean(campaign.data?.client),
  );
  const products = useApiCollection<Product>(
    'campaign-client-products',
    `/products/?client=${campaign.data?.client ?? ''}&is_active=true&page_size=100&ordering=name`,
    accessToken,
    activeTab === 'products-targets' && Boolean(campaign.data?.client),
  );
  const campaignProducts = useApiCollection<CampaignProduct>(
    'campaign-products',
    `/campaign-products/?campaign=${campaignId}&page_size=100&ordering=product__name`,
    accessToken,
  );
  const locationProductTargets = useApiCollection<CampaignLocationProductTarget>(
    'campaign-location-product-targets',
    `/campaign-location-product-targets/?campaign=${campaignId}&page_size=500&ordering=location__venue__name,campaign_product__product__name`,
    accessToken,
    activeTab === 'products-targets' || activeTab === 'store-targets',
  );
  const campaignProductFamilyTargets = useApiCollection<CampaignProductFamilyTarget>(
    'campaign-product-family-targets',
    `/campaign-product-family-targets/?campaign=${campaignId}&page_size=100&ordering=product_family__name`,
    accessToken,
  );
  const locationProductFamilyTargets = useApiCollection<CampaignLocationProductFamilyTarget>(
    'campaign-location-product-family-targets',
    `/campaign-location-product-family-targets/?campaign=${campaignId}&page_size=500&ordering=location__venue__name,campaign_product_family_target__product_family__name`,
    accessToken,
    activeTab === 'products-targets' || activeTab === 'store-targets',
  );
  const operationalDefaults = campaign.data?.operational_defaults;
  const scheduleRuleTemplates = campaign.data?.schedule_rule_templates ?? [];
  const locationRows = locations.data?.results ?? [];
  const storeLocationRows = locationRows.filter((location) => location.site_type === 'store');
  const activityLocationRows = locationRows.filter((location) => location.site_type !== 'store');
  const activitySiteRows = activitySites.data?.results ?? [];
  const activitySiteCatalogueOptions = activitySiteCatalogue.data?.results ?? [];
  const activitySiteCataloguePageCount = Math.max(1, Math.ceil((activitySiteCatalogue.data?.count ?? 0) / 20));
  const activitySessionRows = activitySessions.data?.results ?? [];
  const usableLocationRows = locationRows.filter((location) => location.venue_status === 'open');
  const visibleScheduleLocations = usableLocationRows.filter((location) => (
    `${location.name} ${location.city}`.toLowerCase().includes(scheduleLocationSearch.toLowerCase())
  ));
  const shiftRows = shifts.data?.results ?? [];
  const activityShiftRows = (activityShifts.data?.results ?? []).filter((shift) => (
    activityLocationRows.some((location) => location.id === shift.location)
  ));
  const activityAssignmentRows = campaignAssignments.data?.results ?? [];
  const unconfiguredActivityShifts = activityShiftRows.filter(
    (shift) => !activitySessionRows.some((session) => session.shift === shift.id),
  );
  const selectedActivityShift = activityShiftRows.find(
    (shift) => shift.id === activitySessionDraft.shift,
  );
  const eligibleActivityLeads = activityAssignmentRows.filter((assignment) => (
    ['accepted', 'completed'].includes(assignment.status)
    && selectedActivityShift
    && (
      assignment.shift?.id === selectedActivityShift.id
      || assignment.schedule?.id === selectedActivityShift.schedule
    )
  ));
  const scheduleRows = schedules.data?.results ?? [];
  const scheduleGroupSizes = scheduleRows.reduce<Record<string, number>>((counts, schedule) => {
    if (schedule.group) counts[schedule.group] = (counts[schedule.group] ?? 0) + 1;
    return counts;
  }, {});
  const productRows = products.data?.results ?? [];
  const productTargetRows = campaignProducts.data?.results ?? [];
  const locationProductTargetRows = locationProductTargets.data?.results ?? [];
  const productFamilyTargetRows = campaignProductFamilyTargets.data?.results ?? [];
  const locationProductFamilyTargetRows = locationProductFamilyTargets.data?.results ?? [];
  const publishedSurveyVersions = surveyTemplateVersions.data?.results ?? [];
  const newerPublishedSurveyVersions = campaignSurvey
    ? publishedSurveyVersions.filter((version) => (
      version.template === campaignSurvey.template
      && version.version > campaignSurvey.template_version_number
    ))
    : [];
  const surveyAssignmentRows = surveyAssignments.data?.results ?? [];
  const surveyTalentRows = surveyTalent.data?.results ?? [];
  const surveyTalentPageCount = Math.max(1, Math.ceil((surveyTalent.data?.count ?? 0) / 20));
  const editingFamilyTargetAllocationCount = editingProductFamilyTarget === 'new'
    || editingProductFamilyTarget === null
    ? 0
    : locationProductFamilyTargetRows.filter((target) => (
      target.campaign_product_family_target === editingProductFamilyTarget.id
    )).length;
  const editingProductTargetAllocationCount = editingProductTarget === 'new'
    || editingProductTarget === null
    ? 0
    : locationProductTargetRows.filter((target) => (
      target.campaign_product === editingProductTarget.id
    )).length;
  const productPeriodChangeRequiresConfirmation = editingProductTarget !== 'new'
    && editingProductTarget !== null
    && productTargetDraft.target_period !== editingProductTarget.target_period
    && editingProductTargetAllocationCount > 0;
  const familyPeriodChangeRequiresConfirmation = editingProductFamilyTarget !== 'new'
    && editingProductFamilyTarget !== null
    && productFamilyTargetDraft.target_period !== editingProductFamilyTarget.target_period
    && editingFamilyTargetAllocationCount > 0;
  const venueOptions = venues.data?.results ?? [];
  const venuePageCount = Math.max(1, Math.ceil((venues.data?.count ?? 0) / 20));
  const availableProducts = productRows.filter((product) => (
    !productTargetRows.some((target) => target.product === product.id)
  ));
  const attachedFamilies = Array.from(new Map(
    productTargetRows
      .filter((target) => target.product_family && target.product_family_name)
      .map((target) => [target.product_family as string, {
        id: target.product_family as string,
        name: target.product_family_name as string,
      }]),
  ).values());
  const availableTargetFamilies = attachedFamilies.filter((family) => (
    !productFamilyTargetRows.some((target) => target.product_family === family.id)
  ));
  const individualTargetRows = productTargetRows.filter((target) => target.target_value !== null);
  const storeAllocationTargets: StoreAllocationTarget[] = [
    ...individualTargetRows.map((target) => ({
      key: `product:${target.id}`,
      kind: 'product' as const,
      name: target.product_name,
      detail: [target.product_variant_label, target.product_sku || 'No SKU', 'Product target']
        .filter(Boolean)
        .join(' · '),
      metric: target.target_metric,
      period: target.target_period,
      targetValue: Number(target.target_value),
      target,
    })),
    ...productFamilyTargetRows.map((target) => ({
      key: `family:${target.id}`,
      kind: 'family' as const,
      name: target.product_family_name,
      detail: [target.product_brand_name, `${target.attached_variant_count} variants`, 'Family target']
        .filter(Boolean)
        .join(' · '),
      metric: target.target_metric,
      period: target.target_period,
      targetValue: Number(target.target_value),
      target,
    })),
  ];
  const selectedStoreTarget = storeAllocationTargets.find(
    (target) => target.key === selectedStoreTargetKey,
  ) ?? storeAllocationTargets[0];
  const normalizedStoreTargetSearch = storeTargetSearch.trim().toLowerCase();
  const visibleTargetLocations = storeLocationRows.filter((location) => (
    !normalizedStoreTargetSearch
    || `${location.name} ${location.city}`.toLowerCase().includes(normalizedStoreTargetSearch)
  ));

  const locationMutation = useMutation({
    mutationFn: () => apiRequest<CampaignLocation>(
      editingLocation === 'new' ? '/locations/' : `/locations/${editingLocation?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editingLocation === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify(editingLocation === 'new' ? {
          campaign: campaignId,
          venue: locationDraft.venue,
          allowed_radius_m: Number(locationDraft.allowed_radius_m),
        } : {
          allowed_radius_m: Number(locationDraft.allowed_radius_m),
        }),
      },
    ),
    onSuccess: async () => {
      setEditingLocation(null);
      setLocationDraft(emptyLocation);
      setSelectedVenue(null);
      setVenueSearch('');
      setDebouncedVenueSearch('');
      setVenuePage(1);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-locations'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-available-venues'] }),
      ]);
    },
  });

  const activitySiteMutation = useMutation({
    mutationFn: async () => {
      const payload = editingActivitySite === 'new' ? {
        campaign: campaignId,
        catalogue_site: activitySiteDraft.catalogue_site,
        contact_name: activitySiteDraft.contact_name,
        contact_phone: activitySiteDraft.contact_phone,
      } : {
        contact_name: activitySiteDraft.contact_name,
        contact_phone: activitySiteDraft.contact_phone,
      };
      if (editingActivitySite === 'new') {
        const site = await apiRequest<CampaignActivitySite>('/campaign-activity-sites/', {
          ...(accessToken ? { accessToken } : {}),
          method: 'POST',
          body: JSON.stringify(payload),
        });
        await apiRequest<CampaignLocation>('/locations/', {
          ...(accessToken ? { accessToken } : {}),
          method: 'POST',
          body: JSON.stringify({
            campaign: campaignId,
            activity_site: site.id,
            allowed_radius_m: Number(activitySiteDraft.allowed_radius_m),
          }),
        });
        return site;
      }
      const site = await apiRequest<CampaignActivitySite>(
        `/campaign-activity-sites/${editingActivitySite?.id}/`,
        {
          ...(accessToken ? { accessToken } : {}),
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
      );
      const location = activityLocationRows.find((item) => item.activity_site === site.id);
      if (location) {
        await apiRequest<CampaignLocation>(`/locations/${location.id}/`, {
          ...(accessToken ? { accessToken } : {}),
          method: 'PATCH',
          body: JSON.stringify({
            allowed_radius_m: Number(activitySiteDraft.allowed_radius_m),
          }),
        });
      }
      return site;
    },
    onSuccess: async () => {
      setEditingActivitySite(null);
      setActivitySiteDraft(emptyActivitySite);
      setSelectedActivitySiteCatalogueEntry(null);
      setActivitySiteCatalogueSearch('');
      setDebouncedActivitySiteCatalogueSearch('');
      setActivitySiteCataloguePage(1);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-activity-sites'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-locations'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-available-activity-sites'] }),
      ]);
    },
  });

  const activitySessionMutation = useMutation({
    mutationFn: () => apiRequest<ActivitySession>('/activity-sessions/', {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({
        shift: activitySessionDraft.shift,
        lead: activitySessionDraft.lead,
        backup_lead: activitySessionDraft.backup_lead || null,
        notes: activitySessionDraft.notes,
      }),
    }),
    onSuccess: async () => {
      setEditingActivitySession(false);
      setActivitySessionDraft(emptyActivitySession);
      await queryClient.invalidateQueries({
        queryKey: ['collection', 'campaign-activity-sessions'],
      });
    },
  });

  const campaignSurveyMutation = useMutation({
    mutationFn: () => apiRequest<CampaignSurvey>(
      campaignSurvey ? `/campaign-surveys/${campaignSurvey.id}/` : '/campaign-surveys/',
      {
        ...(accessToken ? { accessToken } : {}),
        method: campaignSurvey ? 'PATCH' : 'POST',
        body: JSON.stringify({
          ...(campaignSurvey ? {} : { campaign: campaignId }),
          template_version: surveyDraft.template_version,
          territory_label: surveyDraft.territory_label,
        }),
      },
    ),
    onSuccess: async () => {
      setEditingSurvey(false);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-surveys'] });
    },
  });

  const activateCampaignSurveyMutation = useMutation({
    mutationFn: () => {
      if (!campaignSurvey) throw new Error('Attach a survey before activating it.');
      return apiRequest<CampaignSurvey>(`/campaign-surveys/${campaignSurvey.id}/activate/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-surveys'] });
    },
  });

  const upgradeCampaignSurveyMutation = useMutation({
    mutationFn: () => {
      if (!campaignSurvey) throw new Error('Attach and activate a survey before upgrading it.');
      return apiRequest<CampaignSurveyUpgradeResult>(`/campaign-surveys/${campaignSurvey.id}/upgrade/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify(surveyUpgradeDraft),
      });
    },
    onSuccess: async () => {
      setUpgradingSurvey(false);
      setSurveyUpgradeDraft(emptySurveyUpgrade);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-surveys'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-survey-assignments'] }),
      ]);
    },
  });

  const surveyAssignmentMutation = useMutation({
    mutationFn: () => {
      if (!campaignSurvey) throw new Error('Activate a survey before authorizing a BA.');
      return apiRequest<SurveyCampaignAssignment>('/survey-campaign-assignments/', {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          campaign_survey: campaignSurvey.id,
          brand_ambassador: surveyAssignmentDraft.brand_ambassador,
          starts_on: surveyAssignmentDraft.starts_on,
          ends_on: surveyAssignmentDraft.ends_on,
          allowed_cities: surveyAssignmentDraft.allowed_cities,
          allowed_areas: commaSeparatedValues(surveyAssignmentDraft.allowed_areas),
        }),
      });
    },
    onSuccess: async () => {
      setAuthorizingSurveyBA(false);
      setSurveyAssignmentDraft(emptySurveyCampaignAssignment);
      setSelectedSurveyTalent(null);
      setSurveyTalentSearch('');
      setDebouncedSurveyTalentSearch('');
      setSurveyTalentPage(1);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-survey-assignments'] });
    },
  });

  const cancelSurveyAssignmentMutation = useMutation({
    mutationFn: (assignment: SurveyCampaignAssignment) => apiRequest<SurveyCampaignAssignment>(
      `/survey-campaign-assignments/${assignment.id}/cancel/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({ reason: surveyAssignmentCancellationReason }),
      },
    ),
    onSuccess: async () => {
      setCancellingSurveyAssignment(null);
      setSurveyAssignmentCancellationReason('');
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-survey-assignments'] });
    },
  });

  const updateSurveyAuthorizationMutation = useMutation({
    mutationFn: () => {
      if (!editingSurveyAssignment) throw new Error('Select a survey authorization to edit.');
      return apiRequest<SurveyCampaignAssignment>(
        `/survey-campaign-assignments/${editingSurveyAssignment.id}/authorization/`,
        {
          ...(accessToken ? { accessToken } : {}),
          method: 'PATCH',
          body: JSON.stringify(surveyAuthorizationDateDraft),
        },
      );
    },
    onSuccess: async () => {
      setEditingSurveyAssignment(null);
      setSurveyAuthorizationDateDraft(emptySurveyAuthorizationDates);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-survey-assignments'] });
    },
  });

  const shiftMutation = useMutation({
    mutationFn: () => apiRequest<CampaignShift>(
      editingShift === 'new' ? '/shifts/' : `/shifts/${editingShift?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editingShift === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify({
          campaign: campaignId,
          ...shiftDraft,
          checkin_open_minutes: Number(shiftDraft.checkin_open_minutes),
          late_after_minutes: Number(shiftDraft.late_after_minutes),
          checkout_grace_minutes: Number(shiftDraft.checkout_grace_minutes),
        }),
      },
    ),
    onSuccess: async () => {
      setEditingShift(null);
      setShiftDraft(emptyShift);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shifts'] });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: () => apiRequest<ShiftSchedule | ShiftSchedule[]>(
      editingSchedule === 'new'
        ? '/shift-schedules/bulk/'
        : isScheduleRevision
          ? `/shift-schedules/${editingSchedule?.id}/revise/`
          : `/shift-schedules/${editingSchedule?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editingSchedule === 'new' || isScheduleRevision ? 'POST' : 'PUT',
        body: JSON.stringify({
          campaign: campaignId,
          ...scheduleDraft,
          rules: scheduleRuleRequestRules(scheduleDraft.rules),
          ...(editingSchedule === 'new'
            ? {}
            : { location: scheduleDraft.locations[0] }),
          ...(isScheduleRevision ? {
            effective_from: revisionEffectiveFrom,
            starts_on: revisionEffectiveFrom,
            reason: revisionReason,
          } : {}),
          checkin_open_minutes: Number(scheduleDraft.checkin_open_minutes),
          late_after_minutes: Number(scheduleDraft.late_after_minutes),
          checkout_grace_minutes: Number(scheduleDraft.checkout_grace_minutes),
        }),
      },
    ),
    onSuccess: async () => {
      setEditingSchedule(null);
      setIsScheduleRevision(false);
      setRevisionImpact(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shift-schedules'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shifts'] }),
      ]);
    },
  });

  const revisionPreviewMutation = useMutation({
    mutationFn: () => apiRequest<ShiftScheduleRevisionImpact>(
      `/shift-schedules/${editingSchedule === 'new' ? '' : editingSchedule?.id}/revision-preview/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          ...scheduleDraft,
          rules: scheduleRuleRequestRules(scheduleDraft.rules),
          effective_from: revisionEffectiveFrom,
          starts_on: revisionEffectiveFrom,
          reason: revisionReason,
          checkin_open_minutes: Number(scheduleDraft.checkin_open_minutes),
          late_after_minutes: Number(scheduleDraft.late_after_minutes),
          checkout_grace_minutes: Number(scheduleDraft.checkout_grace_minutes),
        }),
      },
    ),
    onSuccess: setRevisionImpact,
  });

  const shortenScheduleMutation = useMutation({
    mutationFn: () => apiRequest<{ schedule: ShiftSchedule; impact: ShiftScheduleRevisionImpact }>(
      `/shift-schedules/${shorteningSchedule?.id}/shorten/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({ ends_on: shortenEndsOn, reason: shortenReason }),
      },
    ),
    onSuccess: async () => {
      setShorteningSchedule(null);
      setShortenEndsOn('');
      setShortenReason('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shift-schedules'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shifts'] }),
      ]);
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) => apiRequest<void>(`/shift-schedules/${scheduleId}/`, {
      ...(accessToken ? { accessToken } : {}),
      method: 'DELETE',
    }),
    onSuccess: async () => {
      setDeleteTarget(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shift-schedules'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shifts'] }),
      ]);
    },
  });

  const deleteShiftMutation = useMutation({
    mutationFn: (shiftId: string) => apiRequest<void>(`/shifts/${shiftId}/`, {
      ...(accessToken ? { accessToken } : {}),
      method: 'DELETE',
    }),
    onSuccess: async () => {
      setDeleteTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-shifts'] });
    },
  });

  const detachAttachmentMutation = useMutation({
    mutationFn: (target: DetachTarget) => apiRequest<void>(
      target.kind === 'store'
        ? `/locations/${target.location.id}/`
        : `/campaign-products/${target.product.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'DELETE',
      },
    ),
    onSuccess: async (_data, target) => {
      setDetachTarget(null);
      if (target.kind === 'store') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-locations'] }),
          queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-available-venues'] }),
          queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-location-product-targets'] }),
          queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-location-product-family-targets'] }),
        ]);
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-products'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-client-products'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-product-family-targets'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-location-product-targets'] }),
      ]);
    },
  });

  const productTargetMutation = useMutation({
    mutationFn: () => apiRequest<CampaignProduct>(
      editingProductTarget === 'new'
        ? '/campaign-products/'
        : `/campaign-products/${editingProductTarget?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editingProductTarget === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify(editingProductTarget === 'new' ? {
          campaign: campaignId,
          product: productTargetDraft.product,
          target_value: productTargetDraft.target_value
            ? productTargetDraft.target_value
            : null,
          target_metric: productTargetDraft.target_metric,
          target_period: productTargetDraft.target_period,
        } : {
          target_value: productTargetDraft.target_value
            ? productTargetDraft.target_value
            : null,
          target_metric: productTargetDraft.target_metric,
          target_period: productTargetDraft.target_period,
          confirm_period_change: confirmProductPeriodChange,
        }),
      },
    ),
    onSuccess: async () => {
      setEditingProductTarget(null);
      setProductTargetDraft(emptyProductTarget);
      setConfirmProductPeriodChange(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-products'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-location-product-targets'] }),
      ]);
    },
  });

  const freeSampleDealMutation = useMutation({
    mutationFn: () => {
      if (!editingFreeSampleDeal) throw new Error('Select a qualifying product.');
      const existing = editingFreeSampleDeal.free_sample_deal;
      return apiRequest<CampaignFreeSampleDeal>(
        existing ? `/campaign-free-sample-deals/${existing.id}/` : '/campaign-free-sample-deals/',
        {
          ...(accessToken ? { accessToken } : {}),
          method: existing ? 'PATCH' : 'POST',
          body: JSON.stringify({
            ...(!existing ? { qualifying_product: editingFreeSampleDeal.id } : {}),
            qualifying_quantity: Number(freeSampleDealDraft.qualifying_quantity),
            sample_quantity: Number(freeSampleDealDraft.sample_quantity),
            sample_products: freeSampleDealDraft.sample_products,
            is_active: freeSampleDealDraft.is_active,
          }),
        },
      );
    },
    onSuccess: async () => {
      setEditingFreeSampleDeal(null);
      setFreeSampleDealDraft(emptyFreeSampleDeal);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-products'] });
    },
  });

  const removeFreeSampleDealMutation = useMutation({
    mutationFn: (deal: CampaignFreeSampleDeal) => apiRequest<void>(
      `/campaign-free-sample-deals/${deal.id}/`,
      { ...(accessToken ? { accessToken } : {}), method: 'DELETE' },
    ),
    onSuccess: async () => {
      setEditingFreeSampleDeal(null);
      setFreeSampleDealDraft(emptyFreeSampleDeal);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-products'] });
    },
  });

  const productFamilyTargetMutation = useMutation({
    mutationFn: () => apiRequest<CampaignProductFamilyTarget>(
      editingProductFamilyTarget === 'new'
        ? '/campaign-product-family-targets/'
        : `/campaign-product-family-targets/${editingProductFamilyTarget?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editingProductFamilyTarget === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify(editingProductFamilyTarget === 'new' ? {
          campaign: campaignId,
          product_family: productFamilyTargetDraft.product_family,
          target_value: productFamilyTargetDraft.target_value,
          target_metric: productFamilyTargetDraft.target_metric,
          target_period: productFamilyTargetDraft.target_period,
        } : {
          target_value: productFamilyTargetDraft.target_value,
          target_metric: productFamilyTargetDraft.target_metric,
          target_period: productFamilyTargetDraft.target_period,
          confirm_period_change: confirmFamilyPeriodChange,
        }),
      },
    ),
    onSuccess: async () => {
      setEditingProductFamilyTarget(null);
      setProductFamilyTargetDraft(emptyProductFamilyTarget);
      setConfirmFamilyPeriodChange(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-product-family-targets'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-location-product-family-targets'] }),
      ]);
    },
  });

  const removeProductFamilyTargetMutation = useMutation({
    mutationFn: (target: CampaignProductFamilyTarget) => apiRequest<void>(
      `/campaign-product-family-targets/${target.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'DELETE',
      },
    ),
    onSuccess: async () => {
      setFamilyTargetRemoval(null);
      setEditingProductFamilyTarget(null);
      setProductFamilyTargetDraft(emptyProductFamilyTarget);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['collection', 'campaign-product-family-targets'],
        }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'campaign-products'] }),
        queryClient.invalidateQueries({
          queryKey: ['collection', 'campaign-location-product-family-targets'],
        }),
      ]);
    },
  });

  const locationTargetsMutation = useMutation({
    mutationFn: async () => {
      const existing = new Map(locationProductTargetRows.map((target) => (
        [`${target.location}:${target.campaign_product}`, target]
      )));
      const operations: Array<{
        current: CampaignLocationProductTarget | undefined;
        location: CampaignLocation;
        campaignProduct: CampaignProduct;
        value: number;
      }> = [];

      for (const campaignProduct of productTargetRows) {
        let allocated = 0;
        for (const location of storeLocationRows) {
          const key = `${location.id}:${campaignProduct.id}`;
          const rawValue = (
            locationTargetDrafts[key]
            ?? (existing.get(key) ? String(existing.get(key)?.target_value) : '')
          ).trim();
          const value = rawValue ? Number(rawValue) : 0;
          if (!Number.isFinite(value) || value < 0) {
            throw new Error('Store targets must be numbers of zero or more.');
          }
          allocated += value;
          operations.push({
            current: existing.get(key),
            location,
            campaignProduct,
            value,
          });
        }
        if (campaignProduct.target_value === null && allocated > 0) {
          throw new Error(`Set the parent target for ${campaignProduct.product_name} first.`);
        }
        if (campaignProduct.target_value !== null && allocated > Number(campaignProduct.target_value)) {
          throw new Error(
            `${campaignProduct.product_name} allocations exceed its parent target of ${campaignProduct.target_value}.`,
          );
        }
      }

      const changed = operations.filter(({ current, value }) => Number(current?.target_value ?? 0) !== value);
      const decreases = changed.filter(({ current, value }) => current && value < Number(current.target_value));
      const increases = changed.filter(({ current, value }) => !current || value > Number(current.target_value));
      for (const operation of [...decreases, ...increases]) {
        if (operation.current && operation.value === 0) {
          await apiRequest<void>(`/campaign-location-product-targets/${operation.current.id}/`, {
            ...(accessToken ? { accessToken } : {}),
            method: 'DELETE',
          });
        } else if (operation.current) {
          await apiRequest<CampaignLocationProductTarget>(
            `/campaign-location-product-targets/${operation.current.id}/`,
            {
              ...(accessToken ? { accessToken } : {}),
              method: 'PATCH',
              body: JSON.stringify({ target_value: operation.value }),
            },
          );
        } else if (operation.value > 0) {
          await apiRequest<CampaignLocationProductTarget>('/campaign-location-product-targets/', {
            ...(accessToken ? { accessToken } : {}),
            method: 'POST',
            body: JSON.stringify({
              location: operation.location.id,
              campaign_product: operation.campaignProduct.id,
              target_value: operation.value,
            }),
          });
        }
      }

      const existingFamilyTargets = new Map(locationProductFamilyTargetRows.map((target) => (
        [`${target.location}:${target.campaign_product_family_target}`, target]
      )));
      const familyOperations: Array<{
        current: CampaignLocationProductFamilyTarget | undefined;
        location: CampaignLocation;
        familyTarget: CampaignProductFamilyTarget;
        value: number;
      }> = [];
      for (const familyTarget of productFamilyTargetRows) {
        let allocated = 0;
        for (const location of storeLocationRows) {
          const key = `${location.id}:${familyTarget.id}`;
          const rawValue = (
            locationFamilyTargetDrafts[key]
            ?? (existingFamilyTargets.get(key)
              ? String(existingFamilyTargets.get(key)?.target_value)
              : '')
          ).trim();
          const value = rawValue ? Number(rawValue) : 0;
          if (!Number.isFinite(value) || value < 0) {
            throw new Error('Store family targets must be numbers of zero or more.');
          }
          allocated += value;
          familyOperations.push({
            current: existingFamilyTargets.get(key),
            location,
            familyTarget,
            value,
          });
        }
        if (allocated > Number(familyTarget.target_value)) {
          throw new Error(
            `${familyTarget.product_family_name} allocations exceed its parent target of ${familyTarget.target_value}.`,
          );
        }
      }
      const changedFamilyOperations = familyOperations.filter(
        ({ current, value }) => Number(current?.target_value ?? 0) !== value,
      );
      const familyDecreases = changedFamilyOperations.filter(
        ({ current, value }) => current && value < Number(current.target_value),
      );
      const familyIncreases = changedFamilyOperations.filter(
        ({ current, value }) => !current || value > Number(current.target_value),
      );
      for (const operation of [...familyDecreases, ...familyIncreases]) {
        if (operation.current && operation.value === 0) {
          await apiRequest<void>(
            `/campaign-location-product-family-targets/${operation.current.id}/`,
            {
              ...(accessToken ? { accessToken } : {}),
              method: 'DELETE',
            },
          );
        } else if (operation.current) {
          await apiRequest<CampaignLocationProductFamilyTarget>(
            `/campaign-location-product-family-targets/${operation.current.id}/`,
            {
              ...(accessToken ? { accessToken } : {}),
              method: 'PATCH',
              body: JSON.stringify({ target_value: operation.value }),
            },
          );
        } else if (operation.value > 0) {
          await apiRequest<CampaignLocationProductFamilyTarget>(
            '/campaign-location-product-family-targets/',
            {
              ...(accessToken ? { accessToken } : {}),
              method: 'POST',
              body: JSON.stringify({
                location: operation.location.id,
                campaign_product_family_target: operation.familyTarget.id,
                target_value: operation.value,
              }),
            },
          );
        }
      }
    },
    onSuccess: async () => {
      setLocationTargetDrafts({});
      setLocationFamilyTargetDrafts({});
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['collection', 'campaign-location-product-targets'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['collection', 'campaign-location-product-family-targets'],
        }),
      ]);
    },
  });

  function beginLocation(location: CampaignLocation | 'new') {
    locationMutation.reset();
    setLocationDraft(location === 'new' ? {
      ...emptyLocation,
      allowed_radius_m: String(
        operationalDefaults?.default_allowed_radius_m ?? emptyLocation.allowed_radius_m,
      ),
    } : {
      venue: location.venue ?? '',
      allowed_radius_m: String(location.allowed_radius_m),
    });
    setSelectedVenue(null);
    setVenueSearch('');
    setDebouncedVenueSearch('');
    setVenuePage(1);
    setEditingLocation(location);
  }

  function beginActivitySite(site: CampaignActivitySite | 'new') {
    activitySiteMutation.reset();
    const location = site === 'new'
      ? null
      : activityLocationRows.find((item) => item.activity_site === site.id);
    setActivitySiteDraft(site === 'new' ? emptyActivitySite : {
      catalogue_site: site.catalogue_site ?? '',
      kind: site.kind,
      name: site.name,
      campus_name: site.campus_name,
      address: site.address,
      area: site.area,
      city: site.city,
      province: site.province,
      latitude: site.latitude,
      longitude: site.longitude,
      contact_name: site.contact_name,
      contact_phone: site.contact_phone,
      allowed_radius_m: String(location?.allowed_radius_m ?? 100),
    });
    setSelectedActivitySiteCatalogueEntry(null);
    setActivitySiteCatalogueSearch('');
    setDebouncedActivitySiteCatalogueSearch('');
    setActivitySiteCataloguePage(1);
    setEditingActivitySite(site);
  }

  function beginActivitySession() {
    activitySessionMutation.reset();
    setActivitySessionDraft({
      ...emptyActivitySession,
      shift: unconfiguredActivityShifts[0]?.id ?? '',
    });
    setEditingActivitySession(true);
  }

  function chooseVenue(venue: Venue) {
    setSelectedVenue(venue);
    setLocationDraft((current) => ({ ...current, venue: venue.id }));
  }

  function beginShift(shift: CampaignShift | 'new') {
    shiftMutation.reset();
    setEditingSchedule(null);
    setShiftDraft(shift === 'new' ? {
      ...emptyShift,
      location: usableLocationRows[0]?.id ?? '',
      checkin_open_minutes: String(
        operationalDefaults?.default_checkin_open_minutes ?? emptyShift.checkin_open_minutes,
      ),
      late_after_minutes: String(
        operationalDefaults?.default_late_after_minutes ?? emptyShift.late_after_minutes,
      ),
      checkout_grace_minutes: String(
        operationalDefaults?.default_checkout_grace_minutes ?? emptyShift.checkout_grace_minutes,
      ),
    } : {
      location: shift.location,
      starts_at: localDateTime(shift.starts_at),
      ends_at: localDateTime(shift.ends_at),
      checkin_open_minutes: String(shift.checkin_open_minutes),
      late_after_minutes: String(shift.late_after_minutes),
      checkout_grace_minutes: String(shift.checkout_grace_minutes),
    });
    setEditingShift(shift);
  }

  function beginSchedule(schedule: ShiftSchedule | 'new', revise = false) {
    scheduleMutation.reset();
    revisionPreviewMutation.reset();
    setEditingShift(null);
    setIsScheduleRevision(revise);
    setRevisionImpact(null);
    setScheduleLocationSearch('');
    setRevisionReason('');
    setScheduleDraft(schedule === 'new' ? {
      ...emptySchedule,
      locations: usableLocationRows[0]?.id ? [usableLocationRows[0].id] : [],
      starts_on: campaign.data?.starts_on ?? '',
      ends_on: campaign.data?.ends_on ?? '',
      checkin_open_minutes: String(
        operationalDefaults?.default_checkin_open_minutes ?? emptySchedule.checkin_open_minutes,
      ),
      late_after_minutes: String(
        operationalDefaults?.default_late_after_minutes ?? emptySchedule.late_after_minutes,
      ),
      checkout_grace_minutes: String(
        operationalDefaults?.default_checkout_grace_minutes
        ?? emptySchedule.checkout_grace_minutes,
      ),
      rules: scheduleRuleTemplates[0]
        ? [ruleDraftFromTemplate(scheduleRuleTemplates[0])]
        : [{ ...emptyScheduleRule, weekdays: [] }],
    } : {
      name: schedule.name,
      locations: [schedule.location],
      starts_on: schedule.starts_on,
      ends_on: schedule.ends_on,
      checkin_open_minutes: String(schedule.checkin_open_minutes),
      late_after_minutes: String(schedule.late_after_minutes),
      checkout_grace_minutes: String(schedule.checkout_grace_minutes),
      rules: schedule.rules.map((rule) => ({
        template_number: null,
        name: rule.name,
        weekdays: [...rule.weekdays],
        start_time: rule.start_time.slice(0, 5),
        end_time: rule.end_time.slice(0, 5),
      })),
    });
    if (schedule !== 'new') {
      setRevisionEffectiveFrom(minimumRevisionDate(schedule));
    } else {
      setRevisionEffectiveFrom('');
    }
    setEditingSchedule(schedule);
  }

  function beginProductTarget(target: CampaignProduct | 'new') {
    productTargetMutation.reset();
    setConfirmProductPeriodChange(false);
    setProductTargetDraft(target === 'new' ? {
      ...emptyProductTarget,
      product: availableProducts[0]?.id ?? '',
    } : {
      product: target.product,
      target_value: target.target_value ? String(target.target_value) : '',
      target_metric: target.target_metric,
      target_period: target.target_period,
    });
    setEditingProductTarget(target);
  }

  function beginFreeSampleDeal(target: CampaignProduct) {
    freeSampleDealMutation.reset();
    removeFreeSampleDealMutation.reset();
    const deal = target.free_sample_deal;
    setFreeSampleDealDraft(deal ? {
      qualifying_quantity: String(deal.qualifying_quantity),
      sample_quantity: String(deal.sample_quantity),
      sample_products: deal.sample_product_details.map((product) => product.id),
      is_active: deal.is_active,
    } : { ...emptyFreeSampleDeal, sample_products: [] });
    setEditingFreeSampleDeal(target);
  }

  function beginProductFamilyTarget(target: CampaignProductFamilyTarget | 'new') {
    productFamilyTargetMutation.reset();
    removeProductFamilyTargetMutation.reset();
    setConfirmFamilyPeriodChange(false);
    setProductFamilyTargetDraft(target === 'new' ? {
      ...emptyProductFamilyTarget,
      product_family: availableTargetFamilies[0]?.id ?? '',
    } : {
      product_family: target.product_family,
      target_value: String(target.target_value),
      target_metric: target.target_metric,
      target_period: target.target_period,
    });
    setEditingProductFamilyTarget(target);
  }

  function submitLocation(event: FormEvent) {
    event.preventDefault();
    if (editingLocation === 'new' && !locationDraft.venue) return;
    locationMutation.mutate();
  }

  function closeLocationForm() {
    if (locationMutation.isPending) return;
    setEditingLocation(null);
    setLocationDraft(emptyLocation);
    setSelectedVenue(null);
    setVenueSearch('');
    setDebouncedVenueSearch('');
    setVenuePage(1);
    locationMutation.reset();
  }

  function beginSurveyConfiguration() {
    campaignSurveyMutation.reset();
    setSurveyDraft(campaignSurvey ? {
      template_version: campaignSurvey.template_version,
      territory_label: campaignSurvey.territory_label,
    } : emptySurveyConfiguration);
    setEditingSurvey(true);
  }

  function closeSurveyConfiguration() {
    if (campaignSurveyMutation.isPending) return;
    setEditingSurvey(false);
    setSurveyDraft(emptySurveyConfiguration);
    campaignSurveyMutation.reset();
  }

  function submitSurveyConfiguration(event: FormEvent) {
    event.preventDefault();
    campaignSurveyMutation.mutate();
  }

  function beginSurveyAuthorization() {
    if (!campaignSurvey) return;
    surveyAssignmentMutation.reset();
    setSurveyTalentSearch('');
    setDebouncedSurveyTalentSearch('');
    setSurveyTalentPage(1);
    setSelectedSurveyTalent(null);
    setSurveyAssignmentDraft({
      ...emptySurveyCampaignAssignment,
      starts_on: campaignSurvey.starts_on,
      ends_on: campaignSurvey.ends_on,
    });
    setAuthorizingSurveyBA(true);
  }

  function beginSurveyUpgrade() {
    if (!campaignSurvey || newerPublishedSurveyVersions.length === 0) return;
    upgradeCampaignSurveyMutation.reset();
    setSurveyUpgradeDraft({
      template_version: newerPublishedSurveyVersions[0]?.id ?? '',
      effective_on: '',
    });
    setUpgradingSurvey(true);
  }

  function closeSurveyUpgrade() {
    if (upgradeCampaignSurveyMutation.isPending) return;
    setUpgradingSurvey(false);
    setSurveyUpgradeDraft(emptySurveyUpgrade);
    upgradeCampaignSurveyMutation.reset();
  }

  function submitSurveyUpgrade(event: FormEvent) {
    event.preventDefault();
    upgradeCampaignSurveyMutation.mutate();
  }

  function closeSurveyAuthorization() {
    if (surveyAssignmentMutation.isPending) return;
    setAuthorizingSurveyBA(false);
    setSurveyAssignmentDraft(emptySurveyCampaignAssignment);
    setSurveyTalentSearch('');
    setDebouncedSurveyTalentSearch('');
    setSurveyTalentPage(1);
    setSelectedSurveyTalent(null);
    surveyAssignmentMutation.reset();
  }

  function submitSurveyAuthorization(event: FormEvent) {
    event.preventDefault();
    if (!surveyAssignmentDraft.brand_ambassador) return;
    surveyAssignmentMutation.mutate();
  }

  function beginSurveyAssignmentCancellation(assignment: SurveyCampaignAssignment) {
    cancelSurveyAssignmentMutation.reset();
    setSurveyAssignmentCancellationReason('');
    setCancellingSurveyAssignment(assignment);
  }

  function beginSurveyAuthorizationEdit(assignment: SurveyCampaignAssignment) {
    updateSurveyAuthorizationMutation.reset();
    setSurveyAuthorizationDateDraft({
      starts_on: assignment.starts_on,
      ends_on: assignment.ends_on,
    });
    setEditingSurveyAssignment(assignment);
  }

  function closeSurveyAuthorizationEdit() {
    if (updateSurveyAuthorizationMutation.isPending) return;
    setEditingSurveyAssignment(null);
    setSurveyAuthorizationDateDraft(emptySurveyAuthorizationDates);
    updateSurveyAuthorizationMutation.reset();
  }

  function submitSurveyAuthorizationEdit(event: FormEvent) {
    event.preventDefault();
    updateSurveyAuthorizationMutation.mutate();
  }

  function closeSurveyAssignmentCancellation() {
    if (cancelSurveyAssignmentMutation.isPending) return;
    setCancellingSurveyAssignment(null);
    setSurveyAssignmentCancellationReason('');
    cancelSurveyAssignmentMutation.reset();
  }

  function submitSurveyAssignmentCancellation(event: FormEvent) {
    event.preventDefault();
    if (!cancellingSurveyAssignment) return;
    cancelSurveyAssignmentMutation.mutate(cancellingSurveyAssignment);
  }

  function submitShift(event: FormEvent) {
    event.preventDefault();
    shiftMutation.mutate();
  }

  function submitSchedule(event: FormEvent) {
    event.preventDefault();
    if (isScheduleRevision && revisionImpact === null) {
      revisionPreviewMutation.mutate();
    } else {
      scheduleMutation.mutate();
    }
  }

  function closeScheduleForm() {
    if (scheduleMutation.isPending) return;
    setEditingSchedule(null);
    setScheduleDraft(emptySchedule);
    setIsScheduleRevision(false);
    setRevisionImpact(null);
    setRevisionReason('');
    scheduleMutation.reset();
    revisionPreviewMutation.reset();
  }

  function closeProductTargetForm() {
    if (productTargetMutation.isPending) return;
    setEditingProductTarget(null);
    setProductTargetDraft(emptyProductTarget);
    setConfirmProductPeriodChange(false);
    productTargetMutation.reset();
  }

  function closeFreeSampleDealForm() {
    if (freeSampleDealMutation.isPending || removeFreeSampleDealMutation.isPending) return;
    setEditingFreeSampleDeal(null);
    setFreeSampleDealDraft(emptyFreeSampleDeal);
    freeSampleDealMutation.reset();
    removeFreeSampleDealMutation.reset();
  }

  function closeProductFamilyTargetForm() {
    if (productFamilyTargetMutation.isPending || removeProductFamilyTargetMutation.isPending) return;
    setFamilyTargetRemoval(null);
    setEditingProductFamilyTarget(null);
    setProductFamilyTargetDraft(emptyProductFamilyTarget);
    setConfirmFamilyPeriodChange(false);
    productFamilyTargetMutation.reset();
    removeProductFamilyTargetMutation.reset();
  }

  function updateScheduleRule(index: number, updates: Partial<ScheduleRuleDraft>) {
    setRevisionImpact(null);
    setScheduleDraft((current) => ({
      ...current,
      rules: current.rules.map((rule, ruleIndex) => (
        ruleIndex === index ? { ...rule, ...updates } : rule
      )),
    }));
  }

  function toggleScheduleWeekday(index: number, weekday: string) {
    const rule = scheduleDraft.rules[index];
    if (!rule) return;
    updateScheduleRule(index, {
      weekdays: rule.weekdays.includes(weekday)
        ? rule.weekdays.filter((value) => value !== weekday)
        : [...rule.weekdays, weekday],
    });
  }

  function toggleScheduleLocation(locationId: string) {
    setRevisionImpact(null);
    setScheduleDraft((current) => ({
      ...current,
      locations: current.locations.includes(locationId)
        ? current.locations.filter((value) => value !== locationId)
        : [...current.locations, locationId],
    }));
  }

  function addScheduleRule() {
    setScheduleDraft((current) => {
      const template = scheduleRuleTemplates[current.rules.length] ?? scheduleRuleTemplates[0];
      return {
        ...current,
        rules: [
          ...current.rules,
          template ? ruleDraftFromTemplate(template) : { ...emptyScheduleRule, weekdays: [] },
        ],
      };
    });
  }

  function applyScheduleRuleTemplate(index: number, templateNumber: number | null) {
    if (templateNumber === null) {
      updateScheduleRule(index, { template_number: null });
      return;
    }
    const template = scheduleRuleTemplates.find((entry) => entry.rule_number === templateNumber);
    if (template) updateScheduleRule(index, ruleDraftFromTemplate(template));
  }

  function removeScheduleRule(index: number) {
    setScheduleDraft((current) => ({
      ...current,
      rules: current.rules.filter((_, ruleIndex) => ruleIndex !== index),
    }));
  }

  function requestScheduleDelete(schedule: ShiftSchedule) {
    deleteScheduleMutation.reset();
    deleteShiftMutation.reset();
    setDeleteTarget({ kind: 'schedule', schedule });
  }

  function beginShortenSchedule(schedule: ShiftSchedule) {
    shortenScheduleMutation.reset();
    setShorteningSchedule(schedule);
    setShortenEndsOn(schedule.ends_on);
    setShortenReason('');
  }

  function requestShiftDelete(shift: CampaignShift) {
    deleteScheduleMutation.reset();
    deleteShiftMutation.reset();
    setDeleteTarget({ kind: 'shift', shift });
  }

  function confirmDelete() {
    if (deleteTarget?.kind === 'schedule') {
      deleteScheduleMutation.mutate(deleteTarget.schedule.id);
    } else if (deleteTarget?.kind === 'shift') {
      deleteShiftMutation.mutate(deleteTarget.shift.id);
    }
  }

  function closeDeleteDialog() {
    if (!deleteScheduleMutation.isPending && !deleteShiftMutation.isPending) {
      setDeleteTarget(null);
    }
  }

  function closeDetachDialog() {
    if (detachAttachmentMutation.isPending) return;
    setDetachTarget(null);
    detachAttachmentMutation.reset();
  }

  function submitProductTarget(event: FormEvent) {
    event.preventDefault();
    productTargetMutation.mutate();
  }

  function submitFreeSampleDeal(event: FormEvent) {
    event.preventDefault();
    freeSampleDealMutation.mutate();
  }

  function submitProductFamilyTarget(event: FormEvent) {
    event.preventDefault();
    productFamilyTargetMutation.mutate();
  }

  function individualAllocationValue(
    location: CampaignLocation,
    target: CampaignProduct,
  ) {
    const key = `${location.id}:${target.id}`;
    const existing = locationProductTargetRows.find((item) => (
      item.location === location.id && item.campaign_product === target.id
    ));
    return Number(locationTargetDrafts[key] ?? existing?.target_value ?? 0) || 0;
  }

  function familyAllocationValue(
    location: CampaignLocation,
    target: CampaignProductFamilyTarget,
  ) {
    const key = `${location.id}:${target.id}`;
    const existing = locationProductFamilyTargetRows.find((item) => (
      item.location === location.id
      && item.campaign_product_family_target === target.id
    ));
    return Number(locationFamilyTargetDrafts[key] ?? existing?.target_value ?? 0) || 0;
  }

  const selectedStoreTargetAllocated = selectedStoreTarget
    ? storeLocationRows.reduce((total, location) => (
      total + (selectedStoreTarget.kind === 'product'
        ? individualAllocationValue(location, selectedStoreTarget.target)
        : familyAllocationValue(location, selectedStoreTarget.target))
    ), 0)
    : 0;
  const selectedStoreTargetRemaining = selectedStoreTarget
    ? selectedStoreTarget.targetValue - selectedStoreTargetAllocated
    : 0;

  const tabCounts: Partial<Record<CampaignSetupTab, number>> = {
    ...(locations.data && activitySessions.data
      ? { 'campaign-stores': locations.data.count + activitySessions.data.count }
      : {}),
    ...(schedules.data && shifts.data
      ? { 'shift-schedules': schedules.data.count + shifts.data.count }
      : {}),
    ...(campaignProducts.data ? { 'products-targets': campaignProducts.data.count } : {}),
    ...(campaignProducts.data && campaignProductFamilyTargets.data
      ? { 'store-targets': storeAllocationTargets.length }
      : {}),
    ...(campaignSurveys.data ? { survey: campaignSurveys.data.count } : {}),
  };

  function selectSetupTab(tab: CampaignSetupTab) {
    const hasUnsavedAllocations = Object.keys(locationTargetDrafts).length > 0
      || Object.keys(locationFamilyTargetDrafts).length > 0;
    if (
      activeTab === 'store-targets'
      && tab !== activeTab
      && hasUnsavedAllocations
      && !window.confirm('Discard unsaved store-target changes?')
    ) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams);
  }

  return (
    <>
      <Link className="back-link" to="/campaigns"><ArrowLeft size={15} /> Back to campaigns</Link>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>{campaign.data?.name ?? 'Campaign setup'}</h1>
          <p>Configure retail stores, activity sites, shifts, targets, surveys, and field routes.</p>
        </div>
        <div className="page-heading-actions">
          {capabilities.includes('sales:import') && campaign.data?.can_import_sales ? <><button className="button primary" onClick={() => setShowSalesEntry(true)} type="button">Record sales</button><button className="button secondary" onClick={() => setShowSalesImport(true)} type="button">Import sales CSV</button></> : null}
          <span className="record-count"><MapPin size={16} /> {locationRows.length} locations</span>
        </div>
      </header>

      {showSalesImport && accessToken && campaign.data ? <ManualSalesImportDialog key={`${campaignId}-${selectedWorkspace?.id}`} campaignId={campaignId} accessToken={accessToken} workspaceId={selectedWorkspace?.organizationId ?? ''} startsOn={campaign.data.starts_on} endsOn={campaign.data.ends_on} onClose={() => setShowSalesImport(false)} onSaved={(count) => { setShowSalesImport(false); setSalesImportCount(count); void queryClient.invalidateQueries({ queryKey: ['sales-review'] }); void queryClient.invalidateQueries({ queryKey: ['sales-daily-summary'] }); }} /> : null}
      {showSalesEntry && accessToken && campaign.data ? <ManualSalesEntryDialog key={`${campaignId}-${selectedWorkspace?.id}`} campaignId={campaignId} accessToken={accessToken} workspaceId={selectedWorkspace?.organizationId ?? ''} startsOn={campaign.data.starts_on} endsOn={campaign.data.ends_on} onClose={() => setShowSalesEntry(false)} onSaved={(reference) => { setShowSalesEntry(false); setSavedSaleReference(reference); void queryClient.invalidateQueries({ queryKey: ['sales-review'] }); void queryClient.invalidateQueries({ queryKey: ['sales-daily-summary'] }); }} /> : null}
      {savedSaleReference ? <p role="status">Sales report {savedSaleReference} saved for review. <Link to={`/sales?campaign=${campaignId}`}>Open Sales to review it</Link>.</p> : null}
      {salesImportCount !== null ? <p role="status">{salesImportCount} sales rows saved. <Link to={`/sales?campaign=${campaignId}`}>Open Sales to review them</Link>.</p> : null}

      {campaign.isError ? <p className="form-error page-error">Unable to load this campaign.</p> : null}

      <nav aria-label="Campaign setup sections" className="campaign-setup-tabs campaign-configuration-tabs" role="tablist">
        {campaignSetupTabs.map((tab) => (
          <button
            aria-controls={`campaign-setup-panel-${tab.id}`}
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? 'active' : ''}
            id={`campaign-setup-tab-${tab.id}`}
            key={tab.id}
            onClick={() => selectSetupTab(tab.id)}
            role="tab"
            type="button"
          >
            <span>{tab.label}</span>
            {tabCounts[tab.id] !== undefined ? <small>{tabCounts[tab.id]}</small> : null}
          </button>
        ))}
      </nav>

      {activeTab === 'campaign-stores' ? (
        <div aria-labelledby="campaign-setup-tab-campaign-stores" id="campaign-setup-panel-campaign-stores" role="tabpanel">
      {editingActivitySite ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="activity-site-dialog-title" aria-modal="true" className="confirmation-dialog campaign-setup-form-dialog" role="dialog">
            <button aria-label="Close activity site form" className="icon-button confirmation-close" disabled={activitySiteMutation.isPending} onClick={() => setEditingActivitySite(null)} type="button"><X size={19} /></button>
            <div className="card-heading campaign-setup-form-dialog-heading"><div><h2 id="activity-site-dialog-title">{editingActivitySite === 'new' ? 'Attach activity site' : `Edit ${editingActivitySite.name}`}</h2><p>Select a reusable non-retail site, then configure its campaign-specific radius and contact.</p></div></div>
            <form className="management-form" onSubmit={(event) => { event.preventDefault(); activitySiteMutation.mutate(); }}>
              <div className="form-grid">
                {editingActivitySite === 'new' ? <div className="form-field form-span-2"><span>Activity site</span><StorePicker ariaLabel="Search activity sites" autoOpen emptyMessage="No unattached activity sites match this search." isLoading={activitySiteCatalogue.isLoading} onChange={(value) => {
                  const site = activitySiteCatalogueOptions.find((option) => option.id === value) ?? null;
                  setSelectedActivitySiteCatalogueEntry(site);
                  setActivitySiteDraft(site ? { ...activitySiteDraft, catalogue_site: site.id, kind: site.kind, name: site.name, campus_name: site.campus_name, address: site.address, area: site.area, city: site.city, province: site.province, latitude: site.latitude, longitude: site.longitude } : emptyActivitySite);
                }} onSearchChange={(value) => { setActivitySiteCatalogueSearch(value); setActivitySiteCataloguePage(1); }} options={activitySiteCatalogueOptions.map((site) => ({ id: site.id, name: site.campus_name ? `${site.name} — ${site.campus_name}` : site.name, address: site.address, city: site.city }))} pagination={{ page: activitySiteCataloguePage, pageCount: activitySiteCataloguePageCount, onPageChange: setActivitySiteCataloguePage }} placeholder="Search institution, campus, address or city" required selectedOption={selectedActivitySiteCatalogueEntry ? { id: selectedActivitySiteCatalogueEntry.id, name: selectedActivitySiteCatalogueEntry.campus_name ? `${selectedActivitySiteCatalogueEntry.name} — ${selectedActivitySiteCatalogueEntry.campus_name}` : selectedActivitySiteCatalogueEntry.name, address: selectedActivitySiteCatalogueEntry.address, city: selectedActivitySiteCatalogueEntry.city } : null} value={activitySiteDraft.catalogue_site} /></div> : <div className="form-field form-span-2"><span>Activity site</span><div className="talent-selection"><span><strong>{activitySiteDraft.campus_name ? `${activitySiteDraft.name} — ${activitySiteDraft.campus_name}` : activitySiteDraft.name}</strong><small>{activitySiteDraft.address} · {activitySiteDraft.city}</small></span></div></div>}
                <label>Attendance radius (metres)<input max="1000" min="10" required type="number" value={activitySiteDraft.allowed_radius_m} onChange={(event) => setActivitySiteDraft({ ...activitySiteDraft, allowed_radius_m: event.target.value })} /></label>
                <label>Contact name<input maxLength={160} value={activitySiteDraft.contact_name} onChange={(event) => setActivitySiteDraft({ ...activitySiteDraft, contact_name: event.target.value })} /></label>
                <label>Contact phone<input maxLength={40} value={activitySiteDraft.contact_phone} onChange={(event) => setActivitySiteDraft({ ...activitySiteDraft, contact_phone: event.target.value })} /></label>
              </div>
              {activitySiteMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(activitySiteMutation.error)}</p> : null}
              <div className="form-actions"><button className="button secondary" disabled={activitySiteMutation.isPending} onClick={() => setEditingActivitySite(null)} type="button">Cancel</button><button className="button primary" disabled={activitySiteMutation.isPending || (editingActivitySite === 'new' && !activitySiteDraft.catalogue_site)} type="submit">{activitySiteMutation.isPending ? <InlineSpinner /> : <MapPin size={16} />}{activitySiteMutation.isPending ? 'Saving…' : editingActivitySite === 'new' ? 'Attach activity site' : 'Save activity site'}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      {editingActivitySession ? (
        <div className="confirmation-backdrop" role="presentation">
          <section aria-labelledby="activity-session-dialog-title" aria-modal="true" className="confirmation-dialog campaign-setup-form-dialog" role="dialog">
            <button aria-label="Close activity session form" className="icon-button confirmation-close" disabled={activitySessionMutation.isPending} onClick={() => setEditingActivitySession(false)} type="button"><X size={19} /></button>
            <div className="card-heading campaign-setup-form-dialog-heading"><div><h2 id="activity-session-dialog-title">Create activity session</h2><p>Choose one dated university shift and its report-certifying team lead.</p></div></div>
            <form className="management-form" onSubmit={(event) => { event.preventDefault(); activitySessionMutation.mutate(); }}>
              <label>Dated shift<select required value={activitySessionDraft.shift} onChange={(event) => setActivitySessionDraft({ ...activitySessionDraft, shift: event.target.value, lead: '', backup_lead: '' })}><option value="">Select shift</option>{unconfiguredActivityShifts.map((shift) => <option key={shift.id} value={shift.id}>{shift.location_name} · {dateTimeFormatter.format(new Date(shift.starts_at))}</option>)}</select></label>
              <label>Team lead<select required value={activitySessionDraft.lead} onChange={(event) => setActivitySessionDraft({ ...activitySessionDraft, lead: event.target.value })}><option value="">Select assigned BA</option>{eligibleActivityLeads.map((assignment) => <option key={assignment.id} value={assignment.brand_ambassador}>{assignment.brand_ambassador_name}</option>)}</select></label>
              <label>Backup lead<select value={activitySessionDraft.backup_lead} onChange={(event) => setActivitySessionDraft({ ...activitySessionDraft, backup_lead: event.target.value })}><option value="">No backup</option>{eligibleActivityLeads.filter((assignment) => assignment.brand_ambassador !== activitySessionDraft.lead).map((assignment) => <option key={assignment.id} value={assignment.brand_ambassador}>{assignment.brand_ambassador_name}</option>)}</select></label>
              <label>Session notes<textarea maxLength={2000} value={activitySessionDraft.notes} onChange={(event) => setActivitySessionDraft({ ...activitySessionDraft, notes: event.target.value })} /></label>
              {activitySessionMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(activitySessionMutation.error)}</p> : null}
              <div className="form-actions"><button className="button secondary" disabled={activitySessionMutation.isPending} onClick={() => setEditingActivitySession(false)} type="button">Cancel</button><button className="button primary" disabled={activitySessionMutation.isPending || !activitySessionDraft.shift || !activitySessionDraft.lead} type="submit">{activitySessionMutation.isPending ? <InlineSpinner /> : <Plus size={16} />}{activitySessionMutation.isPending ? 'Creating…' : 'Create session'}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      {editingLocation ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeLocationForm(); }}
          role="presentation"
        >
        <section
          aria-labelledby="campaign-location-form-dialog-title"
          aria-modal="true"
          className="confirmation-dialog campaign-setup-form-dialog"
          role="dialog"
        >
          <button aria-label="Close store form" className="icon-button confirmation-close" disabled={locationMutation.isPending} onClick={closeLocationForm} type="button"><X size={19} /></button>
          <div className="card-heading campaign-setup-form-dialog-heading">
            <div><h2 id="campaign-location-form-dialog-title">{editingLocation === 'new' ? 'Attach store' : `Edit ${editingLocation.name}`}</h2><p>The store supplies trusted coordinates; this campaign controls its attendance radius.</p></div>
          </div>
          <form className="management-form" onSubmit={submitLocation}>
            <div className="form-grid campaign-location-form-grid">
              <div className="form-field talent-selector-field">
                <span>Store</span>
                {editingLocation !== 'new' ? (
                  <div className="talent-selection">
                    <span><strong>{editingLocation.name}</strong><small>{editingLocation.city}</small></span>
                  </div>
                ) : (
                  <StorePicker
                    ariaLabel="Search stores"
                    autoOpen
                    emptyMessage="No unattached stores match this search."
                    isLoading={venues.isLoading}
                    onChange={(venueId) => {
                      const venue = venueOptions.find((option) => option.id === venueId);
                      if (venue) chooseVenue(venue);
                    }}
                    onSearchChange={(value) => {
                      setVenueSearch(value);
                      setVenuePage(1);
                    }}
                    options={venueOptions.map((venue) => ({
                      id: venue.id,
                      name: venue.name,
                      address: venue.address,
                      city: venue.city,
                      chainName: venue.chain_name,
                      storeCode: venue.store_code,
                    }))}
                    pagination={{
                      page: venuePage,
                      pageCount: venuePageCount,
                      onPageChange: setVenuePage,
                    }}
                    required
                    selectedOption={selectedVenue ? {
                      id: selectedVenue.id,
                      name: selectedVenue.name,
                      address: selectedVenue.address,
                      city: selectedVenue.city,
                      chainName: selectedVenue.chain_name,
                      storeCode: selectedVenue.store_code,
                    } : null}
                    value={locationDraft.venue}
                  />
                )}
              </div>
              <label>Allowed radius (metres)<input max="1000" min="10" required type="number" value={locationDraft.allowed_radius_m} onChange={(event) => setLocationDraft({ ...locationDraft, allowed_radius_m: event.target.value })} /></label>
            </div>
            {locationMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(locationMutation.error)}</p> : null}
            <div className="form-actions"><button className="button secondary" disabled={locationMutation.isPending} onClick={closeLocationForm} type="button">Cancel</button><button className="button primary" disabled={locationMutation.isPending || (editingLocation === 'new' && !locationDraft.venue)} type="submit">{locationMutation.isPending ? <InlineSpinner /> : null}{locationMutation.isPending ? 'Saving…' : editingLocation === 'new' ? 'Attach store' : 'Save radius'}</button></div>
          </form>
        </section>
        </div>
      ) : null}

      <section className="section-heading">
        <div><h2>Campaign stores</h2><p>Reusable physical sites authorised for this campaign.</p></div>
        <button className="button secondary" onClick={() => beginLocation('new')} type="button"><Plus size={16} /> Attach store</button>
      </section>
      <DataPanel loading={locations.isLoading} error={locations.isError} empty={storeLocationRows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Store</th><th>Address</th><th>Coordinates</th><th>Campaign radius</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{storeLocationRows.map((location) => <tr key={location.id}>
              <td><strong>{location.name}</strong><small>{location.city}</small>{location.venue_status !== 'open' ? <><StatusPill value={location.venue_status} /><small>Historical work remains available; do not schedule new shifts here.</small></> : null}</td>
              <td>{location.address}</td>
              <td className="muted-cell">{location.latitude}, {location.longitude}</td>
              <td>{location.allowed_radius_m} m</td>
              <td><div className="table-actions"><button className="text-button" onClick={() => beginLocation(location)} type="button"><Pencil size={14} /> Edit</button><button aria-label={`Remove ${location.name} from campaign`} className="text-button danger-text" onClick={() => { detachAttachmentMutation.reset(); setDetachTarget({ kind: 'store', location }); }} type="button"><Trash2 size={14} /> Remove</button></div></td>
            </tr>)}</tbody>
          </table></div>
        </section>
      </DataPanel>

      <section className="section-heading campaign-subsection-heading">
        <div><h2>Activity sites</h2><p>Reusable universities and campuses attached for this campaign. They remain separate from Stores.</p></div>
        <button className="button secondary" onClick={() => beginActivitySite('new')} type="button"><Plus size={16} /> Attach activity site</button>
      </section>
      <DataPanel loading={activitySites.isLoading || locations.isLoading} error={activitySites.isError || locations.isError} empty={activitySiteRows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Institution</th><th>Address</th><th>Coordinates</th><th>Attendance radius</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{activitySiteRows.map((site) => {
              const location = activityLocationRows.find((item) => item.activity_site === site.id);
              return <tr key={site.id}>
                <td><strong>{site.name}</strong><small>{site.campus_name || site.kind}</small></td>
                <td>{site.address}<small>{site.area ? `${site.area} · ` : ''}{site.city}</small></td>
                <td className="muted-cell">{site.latitude}, {site.longitude}</td>
                <td>{location ? `${location.allowed_radius_m} m` : 'Not attached'}</td>
                <td><button className="text-button" onClick={() => beginActivitySite(site)} type="button"><Pencil size={14} /> Edit</button></td>
              </tr>;
            })}</tbody>
          </table></div>
        </section>
      </DataPanel>

      <section className="section-heading campaign-subsection-heading">
        <div><h2>Activity sessions</h2><p>One dated university activity with a lead-certified, automatically generated report.</p></div>
        <button className="button secondary" disabled={unconfiguredActivityShifts.length === 0} onClick={beginActivitySession} type="button"><Plus size={16} /> Create session</button>
      </section>
      <DataPanel loading={activitySessions.isLoading || activityShifts.isLoading || campaignAssignments.isLoading} error={activitySessions.isError || activityShifts.isError || campaignAssignments.isError} empty={activitySessionRows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Session</th><th>Scheduled activity</th><th>Team lead</th><th>Generated totals</th><th>Status</th></tr></thead>
            <tbody>{activitySessionRows.map((session) => <tr key={session.id}>
              <td><strong>{session.activity_site_name}</strong><small>{session.city} · {session.reference}</small></td>
              <td>{dateTimeFormatter.format(new Date(session.starts_at))}<small>to {dateTimeFormatter.format(new Date(session.ends_at))}</small></td>
              <td><strong>{session.lead_name}</strong><small>{session.backup_lead_name ? `Backup: ${session.backup_lead_name}` : 'No backup lead'}</small></td>
              <td><strong>{session.current_totals.student_interceptions} interceptions · {session.current_totals.samples} samples</strong><small>{session.current_totals.completed_surveys} surveys · {session.current_totals.sales_units} sales</small></td>
              <td><StatusPill value={session.status} />{session.latest_certification ? <small>Certified report v{session.latest_certification.revision}</small> : <small>Not yet certified</small>}</td>
            </tr>)}</tbody>
          </table></div>
        </section>
      </DataPanel>
        </div>
      ) : null}

      {activeTab === 'shift-schedules' ? (
        <div aria-labelledby="campaign-setup-tab-shift-schedules" id="campaign-setup-panel-shift-schedules" role="tabpanel">
      {editingSchedule ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeScheduleForm(); }}
          role="presentation"
        >
        <section
          aria-labelledby="shift-schedule-form-dialog-title"
          aria-modal="true"
          className="confirmation-dialog shift-schedule-form-dialog"
          role="dialog"
        >
          <button aria-label="Close schedule form" className="icon-button confirmation-close" disabled={scheduleMutation.isPending} onClick={closeScheduleForm} type="button"><X size={19} /></button>
          <div className="card-heading shift-schedule-form-dialog-heading">
            <div><h2 id="shift-schedule-form-dialog-title">{editingSchedule === 'new' ? 'Create shift schedule' : isScheduleRevision ? `Revise ${editingSchedule.name}` : `Edit ${editingSchedule.name}`}</h2><p>{isScheduleRevision ? 'Apply a new pattern from today or a future date. A same-day revision is allowed only before every affected shift opens for check-in.' : 'Add multiple rules for morning, evening, Friday, and weekend coverage. Dated shifts are generated internally for assignments and attendance.'}</p></div>
          </div>
          <form className="management-form" onSubmit={submitSchedule}>
            <div className={`form-grid${editingSchedule === 'new' ? ' schedule-create-grid' : ''}`}>
              <label className="schedule-name-field">Schedule name<input maxLength={120} required value={scheduleDraft.name} onChange={(event) => { setRevisionImpact(null); setScheduleDraft({ ...scheduleDraft, name: event.target.value }); }} /></label>
              {editingSchedule === 'new' ? (
                <fieldset className="form-field schedule-store-picker">
                  <legend>Locations</legend>
                  <input aria-label="Search locations" placeholder="Search attached locations" type="search" value={scheduleLocationSearch} onChange={(event) => setScheduleLocationSearch(event.target.value)} />
                  <div className="schedule-store-picker-actions"><button className="text-button" onClick={() => { setRevisionImpact(null); setScheduleDraft((current) => ({ ...current, locations: Array.from(new Set([...current.locations, ...visibleScheduleLocations.map((location) => location.id)])) })); }} type="button">Select visible</button><button className="text-button" onClick={() => { setRevisionImpact(null); setScheduleDraft((current) => ({ ...current, locations: [] })); }} type="button">Clear</button></div>
                  <div className="schedule-store-options">{visibleScheduleLocations.map((location) => <label key={location.id}><input checked={scheduleDraft.locations.includes(location.id)} onChange={() => toggleScheduleLocation(location.id)} type="checkbox" /> <span>{location.name}<small>{location.city}</small></span></label>)}</div>
                  <small>{scheduleDraft.locations.length} location{scheduleDraft.locations.length === 1 ? '' : 's'} selected · maximum 50</small>
                </fieldset>
              ) : <label>Location<StorePicker ariaLabel="Location" disabled onChange={() => undefined} options={locationRows.map((location) => ({ id: location.id, name: location.name, address: location.address, city: location.city }))} value={scheduleDraft.locations[0] ?? ''} /></label>}
              {isScheduleRevision ? <label className="schedule-start-field">Revision effective date<input max={editingSchedule === 'new' ? campaign.data?.ends_on : editingSchedule.ends_on} min={editingSchedule !== 'new' ? minimumRevisionDate(editingSchedule) : campaign.data?.starts_on} required type="date" value={revisionEffectiveFrom} onChange={(event) => { setRevisionImpact(null); setRevisionEffectiveFrom(event.target.value); }} /></label> : <label className="schedule-start-field">From date<input max={campaign.data?.ends_on} min={campaign.data?.starts_on} required type="date" value={scheduleDraft.starts_on} onChange={(event) => { setRevisionImpact(null); setScheduleDraft({ ...scheduleDraft, starts_on: event.target.value }); }} /></label>}
              <label className="schedule-end-field">Until date<input max={campaign.data?.ends_on} min={(isScheduleRevision ? revisionEffectiveFrom : scheduleDraft.starts_on) || campaign.data?.starts_on} required type="date" value={scheduleDraft.ends_on} onChange={(event) => { setRevisionImpact(null); setScheduleDraft({ ...scheduleDraft, ends_on: event.target.value }); }} /></label>
              {isScheduleRevision ? <label className="form-span-2">Reason for revision<input maxLength={240} required value={revisionReason} onChange={(event) => { setRevisionImpact(null); setRevisionReason(event.target.value); }} /></label> : null}
              <label className="schedule-checkin-field">Check-in opens (minutes before)<input min="0" required type="number" value={scheduleDraft.checkin_open_minutes} onChange={(event) => { setRevisionImpact(null); setScheduleDraft({ ...scheduleDraft, checkin_open_minutes: event.target.value }); }} /></label>
              <label className="schedule-late-field">Late after (minutes)<input min="0" required type="number" value={scheduleDraft.late_after_minutes} onChange={(event) => { setRevisionImpact(null); setScheduleDraft({ ...scheduleDraft, late_after_minutes: event.target.value }); }} /></label>
              <label className="schedule-checkout-field">Checkout grace (minutes)<input min="0" required type="number" value={scheduleDraft.checkout_grace_minutes} onChange={(event) => { setRevisionImpact(null); setScheduleDraft({ ...scheduleDraft, checkout_grace_minutes: event.target.value }); }} /></label>
            </div>
            <div className="schedule-rules-heading"><div><h3>Schedule rules</h3><p>Rules may share weekdays when their start times differ. Start from a saved template, then adjust any rule detail as needed.</p></div><button className="button secondary" onClick={addScheduleRule} type="button"><Plus size={15} /> Add rule</button></div>
            <div className="schedule-rule-list">{scheduleDraft.rules.map((rule, index) => (
              <fieldset className="schedule-rule-card" key={index}>
                <legend>Rule {index + 1}</legend>
                <div className="schedule-rule-grid">
                  <label className="schedule-rule-template-field">Start from template<select aria-label={`Rule ${index + 1} default template`} value={rule.template_number ?? ''} onChange={(event) => applyScheduleRuleTemplate(index, event.target.value ? Number(event.target.value) : null)}><option value="">Custom rule</option>{scheduleRuleTemplates.map((template) => <option key={template.id} value={template.rule_number}>Template {template.rule_number} — {template.name}</option>)}</select></label>
                  <label className="schedule-rule-name-field">Rule name<input maxLength={80} required value={rule.name} onChange={(event) => updateScheduleRule(index, { name: event.target.value })} /></label>
                  <div className="form-field schedule-rule-weekdays-field"><span>Repeats on</span><div className="weekday-picker">{weekdayOptions.map((weekday) => <label className={`weekday-option${rule.weekdays.includes(weekday.value) ? ' selected' : ''}`} key={weekday.value}><input checked={rule.weekdays.includes(weekday.value)} onChange={() => toggleScheduleWeekday(index, weekday.value)} type="checkbox" /><span>{weekday.label}</span></label>)}</div></div>
                  <div className="schedule-rule-time-fields">
                    <label>Start time<input required type="time" value={rule.start_time} onChange={(event) => updateScheduleRule(index, { start_time: event.target.value })} /></label>
                    <label>End time<input required type="time" value={rule.end_time} onChange={(event) => updateScheduleRule(index, { end_time: event.target.value })} /></label>
                    <small>An end time at or before the start creates an overnight shift.</small>
                  </div>
                </div>
                {scheduleDraft.rules.length > 1 ? <button className="text-button danger-text remove-rule-button" onClick={() => removeScheduleRule(index)} type="button"><X size={14} /> Remove rule</button> : null}
              </fieldset>
            ))}</div>
            {revisionImpact ? <div className="revision-impact" role="status"><strong>Revision impact</strong><span>{revisionImpact.preserved} shifts preserved</span><span>{revisionImpact.created} created</span><span>{revisionImpact.cancelled} future shifts cancelled</span>{revisionImpact.protected_conflicts > 0 ? <span className="danger-text">{revisionImpact.protected_conflicts} worked shifts conflict</span> : <small>Existing assignments will receive a revised offer for acceptance.</small>}</div> : null}
            {scheduleMutation.isError || revisionPreviewMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(scheduleMutation.error ?? revisionPreviewMutation.error)}</p> : null}
            <div className="form-actions"><button className="button secondary" disabled={scheduleMutation.isPending || revisionPreviewMutation.isPending} onClick={closeScheduleForm} type="button">Cancel</button><button className="button primary" disabled={scheduleMutation.isPending || revisionPreviewMutation.isPending || scheduleDraft.locations.length === 0 || scheduleDraft.rules.length === 0 || scheduleDraft.rules.some((rule) => rule.weekdays.length === 0) || (isScheduleRevision && (!revisionEffectiveFrom || !revisionReason || (revisionImpact?.protected_conflicts ?? 0) > 0))} type="submit">{scheduleMutation.isPending || revisionPreviewMutation.isPending ? <InlineSpinner /> : <CalendarRange size={16} />}{scheduleMutation.isPending ? 'Applying…' : revisionPreviewMutation.isPending ? 'Checking…' : isScheduleRevision && revisionImpact === null ? 'Preview revision' : isScheduleRevision ? 'Apply revision' : 'Save schedule'}</button></div>
          </form>
        </section>
        </div>
      ) : null}

      {shorteningSchedule ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !shortenScheduleMutation.isPending) setShorteningSchedule(null); }} role="presentation">
          <section aria-labelledby="shorten-schedule-dialog-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <button aria-label="Close shorten schedule" className="icon-button confirmation-close" disabled={shortenScheduleMutation.isPending} onClick={() => setShorteningSchedule(null)} type="button"><X size={19} /></button>
            <h2 id="shorten-schedule-dialog-title">Shorten {shorteningSchedule.name}</h2>
            <p>Future shifts after the new end date will be cancelled when assigned, while past and worked shifts remain unchanged.</p>
            <form className="management-form" onSubmit={(event) => { event.preventDefault(); shortenScheduleMutation.mutate(); }}>
              <label>New end date<input max={new Date(new Date(`${shorteningSchedule.ends_on}T00:00:00+05:00`).getTime() - 86_400_000).toISOString().slice(0, 10)} min={shorteningSchedule.starts_on} required type="date" value={shortenEndsOn} onChange={(event) => setShortenEndsOn(event.target.value)} /></label>
              <label>Reason<input maxLength={240} required value={shortenReason} onChange={(event) => setShortenReason(event.target.value)} /></label>
              {shortenScheduleMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(shortenScheduleMutation.error)}</p> : null}
              <div className="form-actions"><button className="button secondary" disabled={shortenScheduleMutation.isPending} onClick={() => setShorteningSchedule(null)} type="button">Keep current dates</button><button className="button danger" disabled={shortenScheduleMutation.isPending || !shortenReason || shortenEndsOn >= shorteningSchedule.ends_on} type="submit">{shortenScheduleMutation.isPending ? <InlineSpinner /> : null}{shortenScheduleMutation.isPending ? 'Shortening…' : 'Shorten schedule'}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      <section className="section-heading">
        <div><h2>Shift schedules</h2><p>Grouped store coverage with separate weekday and time rules.</p></div>
        <button className="button secondary" disabled={usableLocationRows.length === 0} onClick={() => beginSchedule('new')} type="button"><CalendarRange size={16} /> Create schedule</button>
      </section>
      <section className="content-card attendance-filter-card">
        <div className="card-heading">
          <div><h3>Find a shift schedule</h3><p>Search by schedule, location or city, then narrow to one location or active date.</p></div>
          <button className="text-button" onClick={() => { setScheduleFilterSearch(''); setDebouncedScheduleFilterSearch(''); setScheduleFilterLocation(''); setScheduleFilterActiveOn(pakistanDateInput()); }} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid">
          <label className="attendance-search-filter">Search<span><Search size={16} /><input aria-label="Search shift schedules" onChange={(event) => setScheduleFilterSearch(event.target.value)} placeholder="Schedule, store, campus or city" value={scheduleFilterSearch} /></span></label>
          <label>Location<select aria-label="Schedule location" onChange={(event) => setScheduleFilterLocation(event.target.value)} value={scheduleFilterLocation}><option value="">All locations</option>{locationRows.map((location) => <option key={location.id} value={location.id}>{location.name} · {location.city}</option>)}</select></label>
          <label>Active on<input aria-label="Schedule active on" onChange={(event) => setScheduleFilterActiveOn(event.target.value)} type="date" value={scheduleFilterActiveOn} /></label>
        </div>
      </section>
      <DataPanel loading={schedules.isLoading} error={schedules.isError} empty={scheduleRows.length === 0}>
        <div className="schedule-card-list">{scheduleRows.map((schedule) => (
          <section className="content-card schedule-card" key={schedule.id}>
            <div className="schedule-card-heading">
              <div><p className="eyebrow">{schedule.location_name} · {schedule.location_city}</p><h3>{schedule.name}</h3><p>{dateFormatter.format(new Date(`${schedule.starts_on}T00:00:00+05:00`))} – {dateFormatter.format(new Date(`${schedule.ends_on}T00:00:00+05:00`))} · {schedule.timezone}{schedule.revision_number > 1 ? ` · Revision ${schedule.revision_number}` : ''}{schedule.group && (scheduleGroupSizes[schedule.group] ?? 0) > 1 ? ` · Shared setup for ${scheduleGroupSizes[schedule.group]} stores` : ''}</p></div>
              <div className="schedule-card-summary"><strong>{schedule.occurrence_count}</strong><span>dated shifts</span><small>{schedule.staffed_occurrence_count} staffed</small></div>
            </div>
            <div className="schedule-rule-summary">{schedule.rules.map((rule) => (
              <div className="schedule-rule-row" key={rule.id}>
                <strong>{rule.name}</strong>
                <span>{rule.weekdays.map((day) => weekdayOptions.find((option) => option.value === day)?.label).join(', ')}</span>
                <span>{formatScheduleTime(rule.start_time)}–{formatScheduleTime(rule.end_time)}</span>
                <small>{rule.occurrence_count} shifts</small>
              </div>
            ))}</div>
            <div className="schedule-card-footer"><span><Clock3 size={14} /> Check-in opens {schedule.checkin_open_minutes}m before · late +{schedule.late_after_minutes}m</span><div className="table-actions">{schedule.has_assignment_history ? <small>Assignment history exists; use a same-day or future revision.</small> : <button className="text-button" onClick={() => beginSchedule(schedule)} type="button"><Pencil size={14} /> Edit schedule</button>}<button className="text-button" onClick={() => beginSchedule(schedule, true)} type="button"><CalendarRange size={14} /> Revise schedule</button><button className="text-button" onClick={() => beginShortenSchedule(schedule)} type="button"><CalendarRange size={14} /> Shorten</button>{!schedule.has_assignment_history ? <button className="text-button danger-text" onClick={() => requestScheduleDelete(schedule)} type="button"><Trash2 size={14} /> Delete schedule</button> : null}</div></div>
          </section>
        ))}</div>
      </DataPanel>

      {editingShift ? (
        <section className="content-card management-panel setup-form-gap">
          <div className="card-heading">
            <div><h2>{editingShift === 'new' ? 'Add individual shift' : `Edit shift at ${editingShift.location_name}`}</h2><p>Use this only for a one-off shift outside a reusable schedule.</p></div>
            <button aria-label="Close shift form" className="icon-button" onClick={() => setEditingShift(null)} type="button"><X size={19} /></button>
          </div>
          <form className="management-form" onSubmit={submitShift}>
            <div className="form-grid">
              <label>Location<StorePicker ariaLabel="Shift location" onChange={(location) => setShiftDraft({ ...shiftDraft, location })} options={(editingShift === 'new' ? usableLocationRows : locationRows).map((location) => ({ id: location.id, name: location.name, address: location.address, city: location.city }))} required value={shiftDraft.location} /></label>
              <label>Check-in opens (minutes before)<input min="0" required type="number" value={shiftDraft.checkin_open_minutes} onChange={(event) => setShiftDraft({ ...shiftDraft, checkin_open_minutes: event.target.value })} /></label>
              <label>Starts at<input required type="datetime-local" value={shiftDraft.starts_at} onChange={(event) => setShiftDraft({ ...shiftDraft, starts_at: event.target.value })} /></label>
              <label>Ends at<input min={shiftDraft.starts_at} required type="datetime-local" value={shiftDraft.ends_at} onChange={(event) => setShiftDraft({ ...shiftDraft, ends_at: event.target.value })} /></label>
              <label>Late after (minutes)<input min="0" required type="number" value={shiftDraft.late_after_minutes} onChange={(event) => setShiftDraft({ ...shiftDraft, late_after_minutes: event.target.value })} /></label>
              <label>Checkout grace (minutes)<input min="0" required type="number" value={shiftDraft.checkout_grace_minutes} onChange={(event) => setShiftDraft({ ...shiftDraft, checkout_grace_minutes: event.target.value })} /></label>
            </div>
            {shiftMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(shiftMutation.error)}</p> : null}
            <div className="form-actions"><button className="button secondary" onClick={() => setEditingShift(null)} type="button">Cancel</button><button className="button primary" disabled={shiftMutation.isPending} type="submit">{shiftMutation.isPending ? <InlineSpinner /> : null}{shiftMutation.isPending ? 'Saving…' : 'Save shift'}</button></div>
          </form>
        </section>
      ) : null}

      <section className="section-heading">
        <div><h2>Individual shifts</h2><p>One-off shifts and older ungrouped entries. Scheduled occurrences remain inside their schedule.</p></div>
        <div className="section-actions">
          {shiftRows.length > 0 ? <button className="button secondary" onClick={() => setShowIndividualShifts((current) => !current)} type="button">{showIndividualShifts ? 'Hide' : 'Show'} {shiftRows.length} shifts</button> : null}
          <button className="button secondary" disabled={usableLocationRows.length === 0} onClick={() => beginShift('new')} type="button"><Plus size={16} /> Add individual shift</button>
        </div>
      </section>
      {showIndividualShifts ? <DataPanel loading={shifts.isLoading} error={shifts.isError} empty={shiftRows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Store</th><th>Starts</th><th>Ends</th><th>Attendance window</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{shiftRows.map((shift) => <tr key={shift.id}>
              <td><strong>{shift.location_name}</strong><small>{shift.location_city}</small></td>
              <td>{dateTimeFormatter.format(new Date(shift.starts_at))}</td>
              <td>{dateTimeFormatter.format(new Date(shift.ends_at))}</td>
              <td><span className="with-icon"><Clock3 size={14} /> Opens {shift.checkin_open_minutes}m before · late +{shift.late_after_minutes}m</span></td>
              <td><div className="table-actions"><button className="text-button" onClick={() => beginShift(shift)} type="button"><Pencil size={14} /> Edit</button><button className="text-button danger-text" onClick={() => requestShiftDelete(shift)} type="button"><Trash2 size={14} /> Delete</button></div></td>
            </tr>)}</tbody>
          </table></div>
        </section>
      </DataPanel> : null}
        </div>
      ) : null}

      {activeTab === 'products-targets' ? (
        <div aria-labelledby="campaign-setup-tab-products-targets" id="campaign-setup-panel-products-targets" role="tabpanel">
      {editingProductFamilyTarget ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeProductFamilyTargetForm(); }}
          role="presentation"
        >
          <section
            aria-labelledby="campaign-product-family-target-form-dialog-title"
            aria-modal="true"
            className="confirmation-dialog campaign-setup-form-dialog"
            role="dialog"
          >
            <button aria-label="Close family target form" className="icon-button confirmation-close" disabled={productFamilyTargetMutation.isPending || removeProductFamilyTargetMutation.isPending} onClick={closeProductFamilyTargetForm} type="button"><X size={19} /></button>
            <div className="card-heading campaign-setup-form-dialog-heading">
              <div><h2 id="campaign-product-family-target-form-dialog-title">{editingProductFamilyTarget === 'new' ? 'Add product family target' : `Edit ${editingProductFamilyTarget.product_family_name} target`}</h2><p>Sales from every attached variant in this family contribute to one shared target.</p></div>
            </div>
            <form className="management-form" onSubmit={submitProductFamilyTarget}>
              <div className="form-grid">
                <label>Product family<select disabled={editingProductFamilyTarget !== 'new'} required value={productFamilyTargetDraft.product_family} onChange={(event) => setProductFamilyTargetDraft({ ...productFamilyTargetDraft, product_family: event.target.value })}><option value="">Select attached family</option>{(editingProductFamilyTarget === 'new' ? availableTargetFamilies : attachedFamilies).map((family) => <option key={family.id} value={family.id}>{family.name}</option>)}</select></label>
                <label>Target metric<select value={productFamilyTargetDraft.target_metric} onChange={(event) => setProductFamilyTargetDraft({ ...productFamilyTargetDraft, target_metric: event.target.value as CampaignProductFamilyTarget['target_metric'] })}>{Object.entries(targetMetricLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label>Target period<select value={productFamilyTargetDraft.target_period} onChange={(event) => { setProductFamilyTargetDraft({ ...productFamilyTargetDraft, target_period: event.target.value as CampaignProductFamilyTarget['target_period'] }); setConfirmFamilyPeriodChange(false); }}>{Object.entries(targetPeriodLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><small className="form-help">A partial reporting month uses its matching calendar-day share of the monthly target.</small></label>
                <label>{productFamilyTargetDraft.target_period === 'monthly' ? 'Monthly target value' : 'Campaign target value'}<input min="0.001" placeholder="e.g. 3796" required step="0.001" type="number" value={productFamilyTargetDraft.target_value} onChange={(event) => setProductFamilyTargetDraft({ ...productFamilyTargetDraft, target_value: event.target.value })} /><small className="form-help">All attached family variants contribute after package conversion.</small></label>
              </div>
              {familyPeriodChangeRequiresConfirmation ? <div className="target-period-confirmation"><strong>Confirm target period change</strong><p>The existing {editingFamilyTargetAllocationCount} store {editingFamilyTargetAllocationCount === 1 ? 'allocation' : 'allocations'} will keep the same values and become {targetPeriodLabels[productFamilyTargetDraft.target_period].toLowerCase()}.</p><label className="checkbox-field"><input checked={confirmFamilyPeriodChange} onChange={(event) => setConfirmFamilyPeriodChange(event.target.checked)} type="checkbox" /> Keep and reinterpret the existing store allocation values</label></div> : null}
              {productFamilyTargetMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(productFamilyTargetMutation.error)}</p> : null}
              {editingProductFamilyTarget !== 'new' && editingFamilyTargetAllocationCount > 0 ? <p className="form-help">Clear this family’s {editingFamilyTargetAllocationCount} store {editingFamilyTargetAllocationCount === 1 ? 'allocation' : 'allocations'} before removing its target.</p> : null}
              <div className="form-actions">
                {editingProductFamilyTarget !== 'new' ? <button className="button danger" disabled={productFamilyTargetMutation.isPending || removeProductFamilyTargetMutation.isPending || locationProductFamilyTargets.isLoading || editingFamilyTargetAllocationCount > 0} onClick={() => setFamilyTargetRemoval(editingProductFamilyTarget)} type="button"><Trash2 size={16} /> Remove family target</button> : null}
                <button className="button secondary" disabled={productFamilyTargetMutation.isPending || removeProductFamilyTargetMutation.isPending} onClick={closeProductFamilyTargetForm} type="button">Cancel</button>
                <button className="button primary" disabled={productFamilyTargetMutation.isPending || removeProductFamilyTargetMutation.isPending || (familyPeriodChangeRequiresConfirmation && !confirmFamilyPeriodChange)} type="submit">{productFamilyTargetMutation.isPending ? <InlineSpinner /> : null}{productFamilyTargetMutation.isPending ? 'Saving…' : 'Save family target'}</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {familyTargetRemoval ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !removeProductFamilyTargetMutation.isPending) setFamilyTargetRemoval(null); }} role="presentation">
          <section aria-labelledby="remove-family-target-dialog-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <div className="confirmation-icon"><Trash2 size={22} /></div>
            <button aria-label="Close family target removal" className="icon-button confirmation-close" disabled={removeProductFamilyTargetMutation.isPending} onClick={() => setFamilyTargetRemoval(null)} type="button"><X size={18} /></button>
            <h2 id="remove-family-target-dialog-title">Remove family target?</h2>
            <p>The shared target for <strong>{familyTargetRemoval.product_family_name}</strong> will be removed. Its products remain attached to the campaign and can receive individual targets again.</p>
            <p className="confirmation-note">Approved sales remain intact, but this target will no longer appear in campaign or performance progress.</p>
            {removeProductFamilyTargetMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(removeProductFamilyTargetMutation.error)}</p> : null}
            <div className="confirmation-actions"><button autoFocus className="button secondary" disabled={removeProductFamilyTargetMutation.isPending} onClick={() => setFamilyTargetRemoval(null)} type="button">Keep target</button><button className="button danger" disabled={removeProductFamilyTargetMutation.isPending} onClick={() => removeProductFamilyTargetMutation.mutate(familyTargetRemoval)} type="button">{removeProductFamilyTargetMutation.isPending ? <InlineSpinner /> : <Trash2 size={16} />}{removeProductFamilyTargetMutation.isPending ? 'Removing…' : 'Remove target'}</button></div>
          </section>
        </div>
      ) : null}

      {editingProductTarget ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeProductTargetForm(); }}
          role="presentation"
        >
        <section
          aria-labelledby="campaign-product-target-form-dialog-title"
          aria-modal="true"
          className="confirmation-dialog campaign-setup-form-dialog"
          role="dialog"
        >
          <button aria-label="Close product target form" className="icon-button confirmation-close" disabled={productTargetMutation.isPending} onClick={closeProductTargetForm} type="button"><X size={19} /></button>
          <div className="card-heading campaign-setup-form-dialog-heading">
            <div><h2 id="campaign-product-target-form-dialog-title">{editingProductTarget === 'new' ? 'Attach product' : `Edit ${editingProductTarget.product_name} target`}</h2><p>Only active products from this campaign’s client catalog can be attached.</p></div>
          </div>
          <form className="management-form" onSubmit={submitProductTarget}>
            <div className="form-grid product-target-form-grid">
              <label className="wide-field">Product<select disabled={editingProductTarget !== 'new'} required value={productTargetDraft.product} onChange={(event) => setProductTargetDraft({ ...productTargetDraft, product: event.target.value })}><option value="">Select product</option>{(editingProductTarget === 'new' ? availableProducts : productRows).map((product) => <option key={product.id} value={product.id}>{product.family_name ? `${product.family_name} · ` : ''}{product.name}{product.variant_label ? ` · ${product.variant_label}` : ''}{product.sku ? ` · ${product.sku}` : ''}</option>)}</select></label>
              <label>Target metric<select value={productTargetDraft.target_metric} onChange={(event) => setProductTargetDraft({ ...productTargetDraft, target_metric: event.target.value as CampaignProduct['target_metric'] })}>{Object.entries(targetMetricLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label>Target period<select value={productTargetDraft.target_period} onChange={(event) => { setProductTargetDraft({ ...productTargetDraft, target_period: event.target.value as CampaignProduct['target_period'] }); setConfirmProductPeriodChange(false); }}>{Object.entries(targetPeriodLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><small className="form-help">A partial reporting month uses its matching calendar-day share of the monthly target.</small></label>
              <label>{productTargetDraft.target_period === 'monthly' ? 'Monthly target value' : 'Campaign target value'}<input min="0.001" placeholder="Optional" step="0.001" type="number" value={productTargetDraft.target_value} onChange={(event) => setProductTargetDraft({ ...productTargetDraft, target_value: event.target.value })} /><small className="form-help">Stored and reported in {targetMetricLabels[productTargetDraft.target_metric]}.</small></label>
            </div>
            {productPeriodChangeRequiresConfirmation ? <div className="target-period-confirmation"><strong>Confirm target period change</strong><p>The existing {editingProductTargetAllocationCount} store {editingProductTargetAllocationCount === 1 ? 'allocation' : 'allocations'} will keep the same values and become {targetPeriodLabels[productTargetDraft.target_period].toLowerCase()}.</p><label className="checkbox-field"><input checked={confirmProductPeriodChange} onChange={(event) => setConfirmProductPeriodChange(event.target.checked)} type="checkbox" /> Keep and reinterpret the existing store allocation values</label></div> : null}
            {productTargetMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(productTargetMutation.error)}</p> : null}
            <div className="form-actions"><button className="button secondary" disabled={productTargetMutation.isPending} onClick={closeProductTargetForm} type="button">Cancel</button><button className="button primary" disabled={productTargetMutation.isPending || (productPeriodChangeRequiresConfirmation && !confirmProductPeriodChange)} type="submit">{productTargetMutation.isPending ? <InlineSpinner /> : null}{productTargetMutation.isPending ? 'Saving…' : 'Save product target'}</button></div>
          </form>
        </section>
        </div>
      ) : null}

      {editingFreeSampleDeal ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeFreeSampleDealForm(); }} role="presentation">
          <section aria-labelledby="free-sample-deal-title" aria-modal="true" className="confirmation-dialog campaign-setup-form-dialog" role="dialog">
            <button aria-label="Close free sample deal form" className="icon-button confirmation-close" onClick={closeFreeSampleDealForm} type="button"><X size={19} /></button>
            <div className="card-heading campaign-setup-form-dialog-heading"><div><h2 id="free-sample-deal-title">Free samples for {editingFreeSampleDeal.product_name}</h2><p>Define how many paid units earn samples. Giveaway SKUs remain outside sales, revenue, and targets.</p></div></div>
            <form className="management-form" onSubmit={submitFreeSampleDeal}>
              <div className="form-grid">
                <label>Paid units required<input min="1" required type="number" value={freeSampleDealDraft.qualifying_quantity} onChange={(event) => setFreeSampleDealDraft({ ...freeSampleDealDraft, qualifying_quantity: event.target.value })} /></label>
                <label>Samples earned<input min="1" required type="number" value={freeSampleDealDraft.sample_quantity} onChange={(event) => setFreeSampleDealDraft({ ...freeSampleDealDraft, sample_quantity: event.target.value })} /></label>
              </div>
              <fieldset><legend>Allowed free-sample SKUs</legend><p className="form-help">Only active client products not attached as sellable campaign products are available.</p><div className="checkbox-list">{availableProducts.map((product) => <label className="checkbox-field" key={product.id}><input checked={freeSampleDealDraft.sample_products.includes(product.id)} onChange={(event) => setFreeSampleDealDraft((current) => ({ ...current, sample_products: event.target.checked ? [...current.sample_products, product.id] : current.sample_products.filter((id) => id !== product.id) }))} type="checkbox" /> {product.name}{product.sku ? ` · ${product.sku}` : ''}</label>)}</div>{availableProducts.length === 0 ? <p className="form-error">Create active free-sample products in the client catalogue and leave them unattached from this campaign.</p> : null}</fieldset>
              <label className="checkbox-field"><input checked={freeSampleDealDraft.is_active} onChange={(event) => setFreeSampleDealDraft({ ...freeSampleDealDraft, is_active: event.target.checked })} type="checkbox" /> Deal is active</label>
              {freeSampleDealMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(freeSampleDealMutation.error)}</p> : null}
              {removeFreeSampleDealMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(removeFreeSampleDealMutation.error)}</p> : null}
              <div className="form-actions">{editingFreeSampleDeal.free_sample_deal ? <button className="button danger" disabled={freeSampleDealMutation.isPending || removeFreeSampleDealMutation.isPending} onClick={() => removeFreeSampleDealMutation.mutate(editingFreeSampleDeal.free_sample_deal as CampaignFreeSampleDeal)} type="button"><Trash2 size={16} /> Remove deal</button> : null}<button className="button secondary" disabled={freeSampleDealMutation.isPending || removeFreeSampleDealMutation.isPending} onClick={closeFreeSampleDealForm} type="button">Cancel</button><button className="button primary" disabled={freeSampleDealMutation.isPending || removeFreeSampleDealMutation.isPending || freeSampleDealDraft.sample_products.length === 0} type="submit">{freeSampleDealMutation.isPending ? <InlineSpinner /> : null}{freeSampleDealMutation.isPending ? 'Saving…' : 'Save deal'}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      <section className="section-heading">
        <div><h2>Products and targets</h2><p>Client catalog items authorized for field sales on this campaign.</p></div>
        <div className="section-actions">
          <button className="button secondary" disabled={availableTargetFamilies.length === 0} onClick={() => beginProductFamilyTarget('new')} type="button"><Plus size={16} /> Add family target</button>
          <button className="button secondary" disabled={availableProducts.length === 0} onClick={() => beginProductTarget('new')} type="button"><Plus size={16} /> Attach product</button>
        </div>
      </section>
      {productFamilyTargetRows.length > 0 ? (
        <section className="content-card data-card product-family-targets-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Product family</th><th>Coverage</th><th>Target value</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{productFamilyTargetRows.map((target) => <tr key={target.id}>
              <td><strong>{target.product_family_name}</strong><small>{target.product_brand_name || 'No brand'}</small></td>
              <td>{target.attached_variant_count} attached {target.attached_variant_count === 1 ? 'variant' : 'variants'}</td>
              <td><span className="with-icon"><PackageCheck size={14} /> {formatTarget(target.target_value, target.target_metric, target.target_period)}</span></td>
              <td><div className="table-actions"><button className="text-button" onClick={() => beginProductFamilyTarget(target)} type="button"><Pencil size={14} /> Edit target</button></div></td>
            </tr>)}</tbody>
          </table></div>
        </section>
      ) : null}
      <DataPanel loading={campaignProducts.isLoading || products.isLoading} error={campaignProducts.isError || products.isError} empty={productTargetRows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Product</th><th>Category</th><th>Unit price</th><th>Target value</th><th>Free samples</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{productTargetRows.map((target) => {
              const coveredByFamilyTarget = target.target_value === null
                && productFamilyTargetRows.some((familyTarget) => (
                  familyTarget.product_family === target.product_family
                ));
              return <tr key={target.id}>
                <td><strong>{target.product_name}</strong><small>{[target.product_family_name, target.product_variant_label, target.product_sku || 'No SKU'].filter(Boolean).join(' · ')}</small></td>
                <td>{target.product_category || '—'}</td>
                <td>{target.product_unit_price ? `PKR ${Number(target.product_unit_price).toLocaleString('en-PK')}` : '—'}</td>
                <td><span className="with-icon"><PackageCheck size={14} /> {coveredByFamilyTarget ? 'Covered by family target' : formatTarget(target.target_value, target.target_metric, target.target_period)}</span></td>
                <td>{target.free_sample_deal ? `${target.free_sample_deal.qualifying_quantity} sold → ${target.free_sample_deal.sample_quantity} free` : 'None'}</td>
                <td><div className="table-actions"><button className="text-button" onClick={() => beginFreeSampleDeal(target)} type="button"><PackageCheck size={14} /> {target.free_sample_deal ? 'Edit deal' : 'Add deal'}</button><button className="text-button" disabled={coveredByFamilyTarget} onClick={() => beginProductTarget(target)} title={coveredByFamilyTarget ? 'Remove the family target before setting an individual product target.' : undefined} type="button"><Pencil size={14} /> Edit target</button><button aria-label={`Remove ${target.product_name} from campaign`} className="text-button danger-text" onClick={() => { detachAttachmentMutation.reset(); setDetachTarget({ kind: 'product', product: target }); }} type="button"><Trash2 size={14} /> Remove</button></div></td>
              </tr>;
            })}</tbody>
          </table></div>
        </section>
      </DataPanel>
        </div>
      ) : null}

      {activeTab === 'store-targets' ? (
        <div aria-labelledby="campaign-setup-tab-store-targets" id="campaign-setup-panel-store-targets" role="tabpanel">
          <section className="section-heading">
            <div><h2>Store targets</h2><p>Allocate individual-product and product-family targets across attached stores. Combined store totals are calculated rather than saved as duplicate targets.</p></div>
          </section>
          <DataPanel
            empty={false}
            error={locations.isError || campaignProducts.isError || campaignProductFamilyTargets.isError}
            loading={locations.isLoading || campaignProducts.isLoading || campaignProductFamilyTargets.isLoading}
          >
          {storeLocationRows.length === 0 ? (
            <section className="content-card campaign-setup-prerequisite">
              <h3>Attach campaign stores first</h3>
              <p>Store targets can be allocated after at least one store is attached to this campaign.</p>
              <button className="button secondary" onClick={() => selectSetupTab('campaign-stores')} type="button">Open campaign stores</button>
            </section>
          ) : storeAllocationTargets.length === 0 ? (
            <section className="content-card campaign-setup-prerequisite">
              <h3>Add a product or family target first</h3>
              <p>Set at least one campaign-total or monthly target before allocating it across stores.</p>
              <button className="button secondary" onClick={() => selectSetupTab('products-targets')} type="button">Open products and targets</button>
            </section>
          ) : (
          <section className="content-card data-card store-product-targets-card">
            <DataPanel loading={locationProductTargets.isLoading || locationProductFamilyTargets.isLoading} error={locationProductTargets.isError || locationProductFamilyTargets.isError} empty={false}>
              {selectedStoreTarget ? <>
                <div className="store-target-editor-header">
                  <div className="store-target-controls">
                    <label className="store-target-field">Target
                      <select
                        aria-label="Target to allocate"
                        value={selectedStoreTarget.key}
                        onChange={(event) => setSelectedStoreTargetKey(event.target.value)}
                      >
                        {storeAllocationTargets.map((target) => (
                          <option key={target.key} value={target.key}>
                            {target.name} · {target.kind === 'family' ? 'Family' : 'Product'} · {formatTarget(target.targetValue, target.metric, target.period)}
                          </option>
                        ))}
                      </select>
                      <small>{selectedStoreTarget.detail}</small>
                    </label>
                    <label className="store-target-field">Find store
                      <span className="store-target-search">
                        <Search aria-hidden="true" size={18} />
                        <input
                          aria-label="Search target stores"
                          onChange={(event) => setStoreTargetSearch(event.target.value)}
                          placeholder="Search store name or city"
                          type="search"
                          value={storeTargetSearch}
                        />
                      </span>
                      <small>{visibleTargetLocations.length} of {storeLocationRows.length} stores shown</small>
                    </label>
                  </div>
                  <div className="store-target-summary" aria-label={`${selectedStoreTarget.name} allocation summary`}>
                    <span><small>{selectedStoreTarget.period === 'monthly' ? 'Monthly target' : 'Campaign target'}</small><strong>{formatTarget(selectedStoreTarget.targetValue, selectedStoreTarget.metric, selectedStoreTarget.period)}</strong></span>
                    <span><small>Allocated</small><strong>{formatTarget(selectedStoreTargetAllocated, selectedStoreTarget.metric, selectedStoreTarget.period)}</strong></span>
                    <span><small>Remaining</small><strong className={selectedStoreTargetRemaining < 0 ? 'danger-text' : ''}>{formatTarget(selectedStoreTargetRemaining, selectedStoreTarget.metric, selectedStoreTarget.period)}</strong></span>
                  </div>
                </div>
                <div className="data-table-wrap"><table aria-label="Store target allocations" className="data-table target-allocation-table">
                  <thead><tr><th>Store</th><th>Allocation</th><th>All targets at store</th></tr></thead>
                  <tbody>
                    {visibleTargetLocations.map((location) => {
                      const target = selectedStoreTarget.target;
                      const key = `${location.id}:${target.id}`;
                      const existing = selectedStoreTarget.kind === 'product'
                        ? locationProductTargetRows.find((item) => (
                          item.location === location.id && item.campaign_product === target.id
                        ))
                        : locationProductFamilyTargetRows.find((item) => (
                          item.location === location.id
                          && item.campaign_product_family_target === target.id
                        ));
                      const value = selectedStoreTarget.kind === 'product'
                        ? locationTargetDrafts[key] ?? (existing ? String(existing.target_value) : '')
                        : locationFamilyTargetDrafts[key] ?? (existing ? String(existing.target_value) : '');
                      const disabled = location.venue_status !== 'open' && !existing;
                      return <tr key={location.id}>
                        <td><strong>{location.name}</strong><small>{location.city}{location.venue_status !== 'open' ? ` · ${location.venue_status}` : ''}</small></td>
                        <td><span className="target-allocation-control">
                          <input
                            aria-label={`${selectedStoreTarget.name} target at ${location.name}`}
                            className="target-allocation-input"
                            disabled={disabled}
                            max={location.venue_status === 'open' ? undefined : existing?.target_value}
                            min="0"
                            placeholder="0"
                            step="0.001"
                            type="number"
                            value={value}
                            onChange={(event) => {
                              if (selectedStoreTarget.kind === 'product') {
                                setLocationTargetDrafts((current) => ({ ...current, [key]: event.target.value }));
                              } else {
                                setLocationFamilyTargetDrafts((current) => ({ ...current, [key]: event.target.value }));
                              }
                            }}
                          />
                          <span>{targetMetricUnits[selectedStoreTarget.metric]}{selectedStoreTarget.period === 'monthly' ? ' / month' : ''}</span>
                        </span></td>
                        <td>{formatTargetTotals([
                          ...individualTargetRows.map((item) => ({
                            metric: item.target_metric,
                            period: item.target_period,
                            value: individualAllocationValue(location, item),
                          })),
                          ...productFamilyTargetRows.map((item) => ({
                            metric: item.target_metric,
                            period: item.target_period,
                            value: familyAllocationValue(location, item),
                          })),
                        ])}</td>
                      </tr>;
                    })}
                    {visibleTargetLocations.length === 0 ? <tr><td className="store-target-empty" colSpan={3}>No attached stores match this search.</td></tr> : null}
                  </tbody>
                </table></div>
              </> : null}
            </DataPanel>
            <div className="store-product-targets-footer">
              {locationTargetsMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(locationTargetsMutation.error)}</p> : null}
              {locationTargetsMutation.isSuccess ? <p className="form-success" role="status">Store targets saved.</p> : null}
              <div className="form-actions"><button className="button primary" disabled={locationTargetsMutation.isPending} onClick={() => locationTargetsMutation.mutate()} type="button">{locationTargetsMutation.isPending ? <InlineSpinner /> : null}{locationTargetsMutation.isPending ? 'Saving…' : 'Save store targets'}</button></div>
            </div>
          </section>
          )}
          </DataPanel>
        </div>
      ) : null}

      {activeTab === 'survey' ? (
        <div aria-labelledby="campaign-setup-tab-survey" id="campaign-setup-panel-survey" role="tabpanel">
          <section className="section-heading">
            <div>
              <h2>Survey</h2>
              <p>Attach a published questionnaire, activate it for BA fieldwork, and upgrade to a later version without changing submitted visits.</p>
            </div>
            {!campaignSurvey || campaignSurvey.status === 'draft' ? (
              <button className="button primary" onClick={beginSurveyConfiguration} type="button">
                <ClipboardList size={16} /> {campaignSurvey ? 'Edit survey setup' : 'Attach survey'}
              </button>
            ) : null}
          </section>

          {campaignSurveys.isError ? <p className="form-error page-error" role="alert">Unable to load the campaign survey configuration.</p> : null}

          {editingSurvey ? (
            <section className="content-card management-panel">
              <div className="card-heading">
                <div>
                  <h3>{campaignSurvey ? 'Edit survey setup' : 'Attach survey'}</h3>
                  <p>Only active, published questionnaire versions belonging to {campaign.data?.client_name ?? 'this campaign client'} are available.</p>
                </div>
              </div>
              <form className="management-form" onSubmit={submitSurveyConfiguration}>
                <div className="form-grid">
                  <label className="form-grid-full">Published questionnaire version
                    <select disabled={surveyTemplateVersions.isLoading || publishedSurveyVersions.length === 0} required value={surveyDraft.template_version} onChange={(event) => setSurveyDraft((current) => ({ ...current, template_version: event.target.value }))}>
                      <option value="">Select published questionnaire</option>
                      {publishedSurveyVersions.map((version) => <option key={version.id} value={version.id}>{[version.template_name, version.brand_name, `Version ${version.version}`].filter(Boolean).join(' · ')}</option>)}
                    </select>
                    {surveyTemplateVersions.isError ? <small className="form-field-error">Unable to load published questionnaires for this client.</small> : null}
                    {!surveyTemplateVersions.isLoading && !surveyTemplateVersions.isError && publishedSurveyVersions.length === 0 ? <small className="form-field-error">No active published questionnaires are available. Create and publish a template first.</small> : null}
                  </label>
                  <p className="form-grid-full inline-help">Uses campaign dates: {campaign.data?.starts_on} to {campaign.data?.ends_on}. These follow changes to the campaign automatically. Each BA keeps their own authorization dates.</p>
                  <label className="form-grid-full">Territory label (optional)<input maxLength={160} placeholder="e.g. Lahore retail zone" value={surveyDraft.territory_label} onChange={(event) => setSurveyDraft((current) => ({ ...current, territory_label: event.target.value }))} /></label>
                </div>
                {campaignSurveyMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(campaignSurveyMutation.error)}</p> : null}
                <div className="form-actions">
                  <button className="button secondary" disabled={campaignSurveyMutation.isPending} onClick={closeSurveyConfiguration} type="button">Cancel</button>
                  <button className="button primary" disabled={campaignSurveyMutation.isPending || publishedSurveyVersions.length === 0} type="submit">{campaignSurveyMutation.isPending ? <InlineSpinner /> : <ClipboardList size={16} />}{campaignSurvey ? 'Save survey setup' : 'Attach survey'}</button>
                </div>
              </form>
            </section>
          ) : campaignSurvey ? (
            <>
              <section className="content-card management-panel">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">{[campaignSurvey.client_name, campaignSurvey.template_brand_name].filter(Boolean).join(' · ')}</p>
                    <h3>{campaignSurvey.template_name}</h3>
                    <p>Questionnaire version {campaignSurvey.template_version_number}{campaignSurvey.territory_label ? ` · ${campaignSurvey.territory_label}` : ''}</p>
                    <p>{campaignSurvey.follows_campaign_start && campaignSurvey.follows_campaign_end ? 'Uses campaign dates' : 'Questionnaire effective dates'}: {campaignSurvey.starts_on} to {campaignSurvey.ends_on}{!campaignSurvey.follows_campaign_start && campaignSurvey.follows_campaign_end ? ' · End date follows campaign' : ''}</p>
                    <p className="inline-help">BA authorization dates are managed separately below. Extending the campaign does not automatically extend BA access.</p>
                  </div>
                  <StatusPill value={campaignSurvey.status} />
                </div>
                {campaignSurvey.status === 'draft' ? (
                  <div className="form-actions">
                    <button className="button primary" disabled={activateCampaignSurveyMutation.isPending} onClick={() => activateCampaignSurveyMutation.mutate()} type="button">{activateCampaignSurveyMutation.isPending ? <InlineSpinner /> : <Check size={16} />} Activate survey</button>
                  </div>
                ) : null}
                {activateCampaignSurveyMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(activateCampaignSurveyMutation.error)}</p> : null}
                {campaignSurvey.status === 'active' ? <p className="form-success" role="status">Survey setup is active. Authorize BAs for this survey before they can submit shop visits.</p> : null}
                {campaignSurvey.status === 'active' && newerPublishedSurveyVersions.length > 0 ? (
                  <div className="form-actions">
                    <button className="button secondary" onClick={beginSurveyUpgrade} type="button">
                      <ClipboardList size={16} /> Upgrade questionnaire
                    </button>
                  </div>
                ) : null}
                {upgradeCampaignSurveyMutation.isSuccess ? (
                  <p className="form-success" role="status">
                    Questionnaire Version {upgradeCampaignSurveyMutation.data.campaign_survey.template_version_number} is scheduled. {upgradeCampaignSurveyMutation.data.copied_assignments} BA authorization{upgradeCampaignSurveyMutation.data.copied_assignments === 1 ? '' : 's'} copied forward{upgradeCampaignSurveyMutation.data.skipped_assignments ? `; ${upgradeCampaignSurveyMutation.data.skipped_assignments} no-longer-eligible authorization${upgradeCampaignSurveyMutation.data.skipped_assignments === 1 ? '' : 's'} skipped.` : '.'}
                  </p>
                ) : null}
              </section>

              {campaignSurveyConfigurations.length > 1 ? (
                <section className="content-card management-panel">
                  <div className="card-heading">
                    <div>
                      <h3>Questionnaire history</h3>
                      <p>Earlier configurations and every visit submitted under them remain available in survey reporting.</p>
                    </div>
                  </div>
                  <div className="survey-version-list">
                    {campaignSurveyConfigurations.slice(1).map((configuration) => (
                      <article className="survey-version-card" key={configuration.id}>
                        <div>
                          <strong>{configuration.template_name} · Version {configuration.template_version_number}</strong>
                          <p>{configuration.starts_on} to {configuration.ends_on}</p>
                        </div>
                        <StatusPill value={configuration.status} />
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              {campaignSurvey.status === 'active' ? (
                <section className="content-card management-panel survey-team-panel">
                  <div className="section-heading">
                    <div>
                      <h3>Survey team</h3>
                      <p>Authorize eligible BAs directly for this questionnaire. This does not create a shift, offer, check-in, or catalogue-store placement.</p>
                    </div>
                    <button className="button primary" onClick={beginSurveyAuthorization} type="button"><Plus size={16} /> Authorize BA</button>
                  </div>
                  {surveyAssignments.isError ? <p className="form-error" role="alert">Unable to load the authorized survey team.</p> : null}
                  {surveyAssignments.isLoading ? <div className="survey-team-loading"><InlineSpinner /> Loading survey team…</div> : null}
                  {!surveyAssignments.isLoading && !surveyAssignments.isError && surveyAssignmentRows.length === 0 ? (
                    <p className="survey-team-empty">No BAs are authorized yet. Select <strong>Authorize BA</strong> to give a verified BA access for the survey dates.</p>
                  ) : null}
                  {!surveyAssignments.isLoading && !surveyAssignments.isError && surveyAssignmentRows.length > 0 ? (
                    <div className="data-table-wrap">
                      <table className="data-table survey-team-table">
                        <thead><tr><th>Brand ambassador</th><th>Authorization dates</th><th>Territory limits</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
                        <tbody>
                          {surveyAssignmentRows.map((assignment) => (
                            <tr key={assignment.id}>
                              <td><strong>{assignment.brand_ambassador_name}</strong></td>
                              <td>{assignment.starts_on} to {assignment.ends_on}</td>
                              <td>{assignment.allowed_cities.length || assignment.allowed_areas.length ? [
                                assignment.allowed_cities.length ? `Cities: ${assignment.allowed_cities.join(', ')}` : '',
                                assignment.allowed_areas.length ? `Areas: ${assignment.allowed_areas.join(', ')}` : '',
                              ].filter(Boolean).join(' · ') : 'No city or area limit'}</td>
                              <td><StatusPill value={assignment.status} /></td>
                              <td>{assignment.status === 'active' ? <div className="section-actions"><button className="text-button" onClick={() => beginSurveyAuthorizationEdit(assignment)} type="button"><Pencil size={14} /> Edit authorization</button><button className="text-button danger-text survey-team-cancel" onClick={() => beginSurveyAssignmentCancellation(assignment)} type="button"><Ban size={14} /> Cancel</button></div> : assignment.cancellation_reason ? <small>{assignment.cancellation_reason}</small> : null}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </section>
              ) : null}
            </>
          ) : (
            <section className="content-card campaign-setup-prerequisite">
              <ClipboardList size={22} />
              <h3>No survey is attached</h3>
              <p>Attach an active published questionnaire owned by this campaign’s client. This does not alter stores, shifts, or sales setup.</p>
            </section>
          )}
        </div>
      ) : null}

      {authorizingSurveyBA && campaignSurvey ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSurveyAuthorization(); }} role="presentation">
          <section aria-labelledby="authorize-survey-ba-dialog-title" aria-modal="true" className="confirmation-dialog survey-team-dialog" role="dialog">
            <button aria-label="Close BA authorization" className="icon-button confirmation-close" disabled={surveyAssignmentMutation.isPending} onClick={closeSurveyAuthorization} type="button"><X size={18} /></button>
            <div className="card-heading">
              <div>
                <h2 id="authorize-survey-ba-dialog-title">Authorize BA for survey</h2>
                <p>Give one eligible BA direct access to this questionnaire. No shift or attendance assignment is created.</p>
              </div>
            </div>
            <form className="management-form" onSubmit={submitSurveyAuthorization}>
              <div className="form-grid">
                <div className="form-field form-grid-full talent-selector-field">
                  <span>Brand ambassador</span>
                  <BrandAmbassadorPicker
                    disabled={surveyAssignmentMutation.isPending}
                    error={surveyTalent.isError}
                    idPrefix="survey-authorization-talent"
                    loading={surveyTalent.isLoading}
                    onClear={() => {
                      setSelectedSurveyTalent(null);
                      setSurveyAssignmentDraft((current) => ({ ...current, brand_ambassador: '' }));
                      setSurveyTalentSearch('');
                      setDebouncedSurveyTalentSearch('');
                      setSurveyTalentPage(1);
                    }}
                    onPageChange={setSurveyTalentPage}
                    onSearchChange={(value) => { setSurveyTalentSearch(value); setSurveyTalentPage(1); }}
                    onSelect={(person) => {
                      setSelectedSurveyTalent(person);
                      setSurveyAssignmentDraft((current) => ({ ...current, brand_ambassador: person.user_id }));
                    }}
                    options={surveyTalentRows}
                    page={surveyTalentPage}
                    pageCount={surveyTalentPageCount}
                    search={surveyTalentSearch}
                    selected={selectedSurveyTalent}
                  />
                </div>
                <label>Authorization starts on<input max={campaignSurvey.ends_on} min={campaignSurvey.starts_on} required type="date" value={surveyAssignmentDraft.starts_on} onChange={(event) => setSurveyAssignmentDraft((current) => ({ ...current, starts_on: event.target.value }))} /></label>
                <label>Authorization ends on<input max={campaignSurvey.ends_on} min={surveyAssignmentDraft.starts_on || campaignSurvey.starts_on} required type="date" value={surveyAssignmentDraft.ends_on} onChange={(event) => setSurveyAssignmentDraft((current) => ({ ...current, ends_on: event.target.value }))} /></label>
                <div className="form-field">
                  <span>Allowed cities <small>Optional</small></span>
                  <CityMultiSelect
                    disabled={surveyAssignmentMutation.isPending}
                    error={pakistanCities.isError}
                    loading={pakistanCities.isLoading}
                    onChange={(allowed_cities) => setSurveyAssignmentDraft((current) => ({ ...current, allowed_cities }))}
                    options={pakistanCities.data?.results ?? []}
                    value={surveyAssignmentDraft.allowed_cities}
                  />
                </div>
                <label>Allowed areas <small>Optional, comma-separated</small><input aria-label="Allowed areas" maxLength={4096} placeholder="e.g. Gulberg, DHA" value={surveyAssignmentDraft.allowed_areas} onChange={(event) => setSurveyAssignmentDraft((current) => ({ ...current, allowed_areas: event.target.value }))} /></label>
              </div>
              <p className="inline-help">Leave city and area blank to allow this BA to survey any shop within the campaign’s authorized dates.</p>
              {surveyAssignmentMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(surveyAssignmentMutation.error)}</p> : null}
              <div className="form-actions">
                <button className="button secondary" disabled={surveyAssignmentMutation.isPending} onClick={closeSurveyAuthorization} type="button">Cancel</button>
                <button className="button primary" disabled={surveyAssignmentMutation.isPending || !surveyAssignmentDraft.brand_ambassador} type="submit">{surveyAssignmentMutation.isPending ? <InlineSpinner /> : <Check size={16} />}{surveyAssignmentMutation.isPending ? 'Authorizing…' : 'Authorize BA'}</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {upgradingSurvey && campaignSurvey ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSurveyUpgrade(); }} role="presentation">
          <section aria-labelledby="upgrade-survey-dialog-title" aria-modal="true" className="confirmation-dialog survey-team-dialog" role="dialog">
            <button aria-label="Close questionnaire upgrade" className="icon-button confirmation-close" disabled={upgradeCampaignSurveyMutation.isPending} onClick={closeSurveyUpgrade} type="button"><X size={18} /></button>
            <div className="card-heading">
              <div>
                <h2 id="upgrade-survey-dialog-title">Upgrade questionnaire</h2>
                <p>Version {campaignSurvey.template_version_number} will remain in the reporting history. Eligible BA authorizations will be copied to the selected version automatically.</p>
              </div>
            </div>
            <form className="management-form" onSubmit={submitSurveyUpgrade}>
              <div className="form-grid">
                <label className="form-grid-full">New published questionnaire version
                  <select required value={surveyUpgradeDraft.template_version} onChange={(event) => setSurveyUpgradeDraft((current) => ({ ...current, template_version: event.target.value }))}>
                    {newerPublishedSurveyVersions.map((version) => <option key={version.id} value={version.id}>Version {version.version}{version.notes ? ` · ${version.notes}` : ''}</option>)}
                  </select>
                </label>
                <label className="form-grid-full">Effective on
                  <input aria-label="Effective on" max={campaignSurvey.ends_on} min={[dateInputDaysAfter(pakistanDateInput(), 1), dateInputDaysAfter(campaignSurvey.starts_on, 1)].sort().at(-1)} required type="date" value={surveyUpgradeDraft.effective_on} onChange={(event) => setSurveyUpgradeDraft((current) => ({ ...current, effective_on: event.target.value }))} />
                  <small>Choose a future date. BA mobile apps receive the new questionnaire on their next refresh from this date.</small>
                </label>
              </div>
              {upgradeCampaignSurveyMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(upgradeCampaignSurveyMutation.error)}</p> : null}
              <div className="form-actions">
                <button className="button secondary" disabled={upgradeCampaignSurveyMutation.isPending} onClick={closeSurveyUpgrade} type="button">Cancel</button>
                <button className="button primary" disabled={upgradeCampaignSurveyMutation.isPending} type="submit">{upgradeCampaignSurveyMutation.isPending ? <InlineSpinner /> : <Check size={16} />}{upgradeCampaignSurveyMutation.isPending ? 'Upgrading…' : 'Upgrade survey'}</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {editingSurveyAssignment && campaignSurvey ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSurveyAuthorizationEdit(); }} role="presentation">
          <section aria-labelledby="edit-survey-authorization-dialog-title" aria-modal="true" className="confirmation-dialog survey-team-dialog" role="dialog">
            <button aria-label="Close authorization editor" className="icon-button confirmation-close" disabled={updateSurveyAuthorizationMutation.isPending} onClick={closeSurveyAuthorizationEdit} type="button"><X size={18} /></button>
            <div className="card-heading"><div><h2 id="edit-survey-authorization-dialog-title">Edit authorization</h2><p>Change survey access dates for <strong>{editingSurveyAssignment.brand_ambassador_name}</strong>. Submitted surveys must remain inside the authorization period.</p></div></div>
            <form className="management-form" onSubmit={submitSurveyAuthorizationEdit}>
              <div className="form-grid">
                <label>Authorization starts on<input max={campaignSurvey.ends_on} min={campaignSurvey.starts_on} required type="date" value={surveyAuthorizationDateDraft.starts_on} onChange={(event) => setSurveyAuthorizationDateDraft((current) => ({ ...current, starts_on: event.target.value }))} /></label>
                <label>Authorization ends on<input max={campaignSurvey.ends_on} min={surveyAuthorizationDateDraft.starts_on || campaignSurvey.starts_on} required type="date" value={surveyAuthorizationDateDraft.ends_on} onChange={(event) => setSurveyAuthorizationDateDraft((current) => ({ ...current, ends_on: event.target.value }))} /></label>
              </div>
              <p className="inline-help">Dates must remain within {campaignSurvey.starts_on} to {campaignSurvey.ends_on}. The BA receives the change on the next app refresh.</p>
              {updateSurveyAuthorizationMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(updateSurveyAuthorizationMutation.error)}</p> : null}
              <div className="form-actions"><button className="button secondary" disabled={updateSurveyAuthorizationMutation.isPending} onClick={closeSurveyAuthorizationEdit} type="button">Cancel</button><button className="button primary" disabled={updateSurveyAuthorizationMutation.isPending} type="submit">{updateSurveyAuthorizationMutation.isPending ? <InlineSpinner /> : <Save size={16} />}{updateSurveyAuthorizationMutation.isPending ? 'Saving…' : 'Save authorization'}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      {cancellingSurveyAssignment ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSurveyAssignmentCancellation(); }} role="presentation">
          <section aria-labelledby="cancel-survey-authorization-dialog-title" aria-modal="true" className="confirmation-dialog survey-team-dialog" role="dialog">
            <button aria-label="Close survey authorization cancellation" className="icon-button confirmation-close" disabled={cancelSurveyAssignmentMutation.isPending} onClick={closeSurveyAssignmentCancellation} type="button"><X size={18} /></button>
            <div className="confirmation-icon"><Ban size={22} /></div>
            <h2 id="cancel-survey-authorization-dialog-title">Cancel survey authorization?</h2>
            <p><strong>{cancellingSurveyAssignment.brand_ambassador_name}</strong> will no longer be able to submit shop visits for this campaign survey.</p>
            <form className="management-form" onSubmit={submitSurveyAssignmentCancellation}>
              <label>Cancellation reason<textarea maxLength={240} required value={surveyAssignmentCancellationReason} onChange={(event) => setSurveyAssignmentCancellationReason(event.target.value)} /></label>
              {cancelSurveyAssignmentMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(cancelSurveyAssignmentMutation.error)}</p> : null}
              <div className="confirmation-actions">
                <button autoFocus className="button secondary" disabled={cancelSurveyAssignmentMutation.isPending} onClick={closeSurveyAssignmentCancellation} type="button">Keep authorization</button>
                <button className="button danger" disabled={cancelSurveyAssignmentMutation.isPending} type="submit">{cancelSurveyAssignmentMutation.isPending ? <InlineSpinner /> : <Ban size={16} />}{cancelSurveyAssignmentMutation.isPending ? 'Cancelling…' : 'Cancel authorization'}</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {activeTab === 'merchandising-routes' ? (
        <div aria-labelledby="campaign-setup-tab-merchandising-routes" id="campaign-setup-panel-merchandising-routes" role="tabpanel">
          {campaign.data ? (
            <MerchandisingRoutesPanel campaign={campaign.data} />
          ) : (
            <section className="content-card campaign-setup-prerequisite">
              <Route size={22} />
              <h3>Loading campaign route planning</h3>
            </section>
          )}
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDeleteDialog(); }}>
          <section aria-labelledby="delete-dialog-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <div className="confirmation-icon"><Trash2 size={22} /></div>
            <button aria-label="Close delete confirmation" className="icon-button confirmation-close" disabled={deleteScheduleMutation.isPending || deleteShiftMutation.isPending} onClick={closeDeleteDialog} type="button"><X size={18} /></button>
            <h2 id="delete-dialog-title">{deleteTarget.kind === 'schedule' ? 'Delete shift schedule?' : 'Delete individual shift?'}</h2>
            {deleteTarget.kind === 'schedule' ? (
              <p><strong>{deleteTarget.schedule.name}</strong> and its {deleteTarget.schedule.occurrence_count} unstaffed dated shifts will be permanently removed.</p>
            ) : (
              <p>The shift at <strong>{deleteTarget.shift.location_name}</strong> starting {dateTimeFormatter.format(new Date(deleteTarget.shift.starts_at))} will be permanently removed.</p>
            )}
            <p className="confirmation-note">This action cannot be undone. Staffed records are protected and cannot be deleted.</p>
            {deleteScheduleMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(deleteScheduleMutation.error)}</p> : null}
            {deleteShiftMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(deleteShiftMutation.error)}</p> : null}
            <div className="confirmation-actions"><button autoFocus className="button secondary" disabled={deleteScheduleMutation.isPending || deleteShiftMutation.isPending} onClick={closeDeleteDialog} type="button">Keep it</button><button className="button danger" disabled={deleteScheduleMutation.isPending || deleteShiftMutation.isPending} onClick={confirmDelete} type="button">{deleteScheduleMutation.isPending || deleteShiftMutation.isPending ? <InlineSpinner /> : <Trash2 size={16} />}{deleteScheduleMutation.isPending || deleteShiftMutation.isPending ? 'Deleting…' : 'Delete permanently'}</button></div>
          </section>
        </div>
      ) : null}

      {detachTarget ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDetachDialog(); }}>
          <section aria-labelledby="detach-dialog-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <div className="confirmation-icon"><Trash2 size={22} /></div>
            <button aria-label="Close detach confirmation" className="icon-button confirmation-close" disabled={detachAttachmentMutation.isPending} onClick={closeDetachDialog} type="button"><X size={18} /></button>
            <h2 id="detach-dialog-title">Detach {detachTarget.kind}?</h2>
            {detachTarget.kind === 'store' ? (
              <p><strong>{detachTarget.location.name}</strong> will be removed from this campaign. Its store-target allocations will also be removed.</p>
            ) : (
              <p><strong>{detachTarget.product.product_name}</strong>, its campaign target and its store-target allocations will be removed from this campaign.</p>
            )}
            <p className="confirmation-note">The catalogue record remains available for other campaigns. Operational history and in-use attachments are protected.</p>
            {detachAttachmentMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(detachAttachmentMutation.error)}</p> : null}
            <div className="confirmation-actions"><button autoFocus className="button secondary" disabled={detachAttachmentMutation.isPending} onClick={closeDetachDialog} type="button">Keep attached</button><button className="button danger" disabled={detachAttachmentMutation.isPending} onClick={() => detachAttachmentMutation.mutate(detachTarget)} type="button">{detachAttachmentMutation.isPending ? <InlineSpinner /> : <Trash2 size={16} />}{detachAttachmentMutation.isPending ? 'Removing…' : `Detach ${detachTarget.kind}`}</button></div>
          </section>
        </div>
      ) : null}
    </>
  );
}
